import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Popover from '@mui/material/Popover';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import AutoModeOutlinedIcon from '@mui/icons-material/AutoModeOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import EditCalendarOutlinedIcon from '@mui/icons-material/EditCalendarOutlined';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import {
  BankHoursRequest,
  TimeClockDay,
  TimeClockEntry,
  TimeJustification,
  TimeJustificationType,
  TimeclockDashboardSummary,
  TimeCorrectionRequest,
  TimeCorrectionRequestStatus,
  TimeCorrectionApplyMode,
  TimeClockEntryKind,
  PaginatedResponse,
} from '../types';
import PremiumSelect from '../components/PremiumSelect';
import ConfirmModal from '../components/ConfirmModal';
import TcPagination from '../components/TcPagination';
import TcAccordionSection, { tcAccordionListSubtitle } from '../components/TcAccordionSection';
import ApprovalStatusFilter, {
  ApprovalStatusFilterValue,
  buildApprovalFilterOptions,
  countByApprovalFilter,
  matchesJustificationStatus,
  matchesSimpleApprovalStatus,
} from '../components/ApprovalStatusFilter';
import InvoiceHistoryPanel from '../components/InvoiceHistoryPanel';
import InvoiceManagementTabs from '../components/InvoiceManagementTabs';
import MyInvoiceObligationSummary from '../components/MyInvoiceObligationSummary';
import PageContainer from '../components/ui/PageContainer';
import StatusChip, { StatusTone } from '../components/ui/StatusChip';
import PunchStepCard, { PunchKind } from '../components/ui/PunchStepCard';
import { formatTimeclockApplyMode, formatTimeclockEntryKind } from '../utils/timeclockEntryKind';
import { BonusEligibilityLike, formatBonusUnjustifiedCounter } from '../utils/bonusEligibilityUi';
import { computeLiveDayBalanceMinutes } from '../utils/timeclockLiveBalance';
import { reaisInputToCents } from '../utils/invoiceSearch';

type MonthlyBonusRow = {
  userId: string;
  name: string;
  bonus: BonusEligibilityLike;
};

function formatMinutes(m: number): string {
  const sign = m < 0 ? '−' : '';
  const a = Math.abs(m);
  const h = Math.floor(a / 60);
  const min = a % 60;
  if (h === 0) return `${sign}${min}min`;
  if (min === 0) return `${sign}${h}h`;
  return `${sign}${h}h ${min}min`;
}

/** Tom (StatusChip) do saldo conforme o valor: negativo = danger, positivo = success. */
function balanceTone(minutes: number): StatusTone {
  if (minutes < 0) return 'danger';
  if (minutes > 0) return 'success';
  return 'neutral';
}

/** Cor de texto do saldo (para valores exibidos sem chip). */
function balanceColor(minutes: number): string {
  if (minutes < 0) return 'error.main';
  if (minutes > 0) return 'success.main';
  return 'text.primary';
}

const BalanceText: React.FC<{ minutes: number }> = ({ minutes }) => (
  <Box component="span" sx={{ color: balanceColor(minutes), fontWeight: 700 }}>
    {formatMinutes(minutes)}
  </Box>
);

function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDateBr(iso: string): string {
  const [y, mo, d] = iso.slice(0, 10).split('-');
  return `${d}/${mo}/${y}`;
}

function formatYmdDashToBr(ymd: string): string {
  const [y, mo, d] = ymd.split('-');
  if (!y || !mo || !d) return ymd;
  return `${d}/${mo}/${y}`;
}

/** Data e hora no fuso America/Sao_Paulo para edição (inputs date + time). */
function isoToBrazilDateAndTime(iso: string): { workDate: string; timeHM: string } {
  const d = new Date(iso);
  const workDate = d.toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const hh = parts.find((p) => p.type === 'hour')?.value ?? '00';
  const mm = parts.find((p) => p.type === 'minute')?.value ?? '00';
  return { workDate, timeHM: `${hh.padStart(2, '0')}:${mm.padStart(2, '0')}` };
}

/** Interpreta data (YYYY-MM-DD) + hora (HH:mm) como horário de São Paulo (offset fixo -03:00). */
function brazilDateTimeToIso(workDate: string, timeHM: string): string {
  return `${workDate}T${timeHM}:00-03:00`;
}

/** Dia sem entrada/saída e sem falta — não exibir (exceto dias zerados manualmente). */
function isVisibleTimeClockDay(d: TimeClockDay): boolean {
  return !!(d.firstCheckInAt || d.lastCheckOutAt || d.isAbsence || d.statusLabel === 'Zerado');
}

const ENTRY_KIND_OPTIONS = [
  { value: 'check_in', label: 'Entrada' },
  { value: 'check_out', label: 'Saída' },
  { value: 'lunch_out', label: 'Saída almoço' },
  { value: 'lunch_in', label: 'Volta almoço' },
];

const JUST_TYPES: { value: TimeJustificationType; label: string }[] = [
  { value: 'falta', label: 'Falta' },
  { value: 'atraso', label: 'Atraso' },
  { value: 'saida_antecipada', label: 'Saída antecipada' },
  { value: 'outro', label: 'Outro' },
];

const statusJustPt: Record<string, string> = {
  sent: 'Enviado',
  under_review: 'Em análise',
  approved: 'Aprovado',
  rejected: 'Rejeitado',
};

const statusBankPt: Record<string, string> = {
  pending: 'Pendente',
  approved: 'Aprovado',
  rejected: 'Rejeitado',
};

const statusCorrectionPt: Record<string, string> = {
  pending: 'Pendente',
  approved: 'Aprovada',
  rejected: 'Rejeitada',
};

/** Tom do StatusChip para cada situação (aprovada = success, rejeitada = danger, pendente = warning). */
function approvalTone(status: string): StatusTone {
  if (status === 'approved') return 'success';
  if (status === 'rejected') return 'danger';
  if (status === 'under_review') return 'info';
  return 'warning';
}

const CORRECTION_KIND_OPTIONS = [
  { value: 'check_in', label: 'Entrada' },
  { value: 'check_out', label: 'Saída' },
  { value: 'lunch_out', label: 'Saída almoço' },
  { value: 'lunch_in', label: 'Volta almoço' },
];

const CORRECTION_EXTRA_KIND_OPTIONS = CORRECTION_KIND_OPTIONS.filter(
  (o) => o.value === 'check_in' || o.value === 'check_out',
);

const CORRECTION_APPLY_MODE_OPTIONS = [
  { value: 'adjust_existing', label: 'Corrigir batida já existente' },
  { value: 'add_extra', label: 'Adicionar batida extra (hora extra / reabertura)' },
];

const HR_CORRECTION_DECISION_OPTIONS = [
  { value: 'pending', label: 'Pendente' },
  { value: 'approved', label: 'Aprovada' },
  { value: 'rejected', label: 'Rejeitada' },
];

const MONTH_SELECT_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1).padStart(2, '0'),
}));

const BANK_DISPOSITION_OPTIONS = [
  { value: 'compensation', label: 'Compensação' },
  { value: 'payment', label: 'Pagamento' },
];

const BANK_COMPENSATION_MODE_OPTIONS = [
  { value: 'single_day', label: 'Dia único' },
  { value: 'range', label: 'Período (intervalo)' },
];

type BankAmountUnit = 'minutes' | 'hours' | 'days';

/** 1 dia de banco = jornada líquida padrão (8h). */
const BANK_DAY_MINUTES = 8 * 60;

const BANK_AMOUNT_UNIT_OPTIONS = [
  { value: 'minutes', label: 'Minutos' },
  { value: 'hours', label: 'Horas' },
  { value: 'days', label: 'Dias' },
];

function bankAmountToMinutes(amount: number, unit: BankAmountUnit): number {
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  if (unit === 'hours') return Math.round(amount * 60);
  if (unit === 'days') return Math.round(amount * BANK_DAY_MINUTES);
  return Math.round(amount);
}

/** Remove zeros à esquerda ao digitar (mantém "0.5"). */
function normalizeBankAmountInput(raw: string): string {
  if (raw === '' || raw === '.') return raw;
  if (/^0\.\d*$/.test(raw)) return raw;
  if (/^0+$/.test(raw)) return '0';
  return raw.replace(/^0+(?=\d)/, '');
}

function parseBankAmount(raw: string): number {
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function formatBankCompensationLabel(r: {
  disposition: string;
  compensationMode?: string | null;
  compensationDate?: string | null;
  compensationStartDate?: string | null;
  compensationEndDate?: string | null;
  compensationDeadline?: string | null;
}): string {
  if (r.disposition !== 'compensation') return 'Pagamento';
  if (r.compensationMode === 'single_day' && r.compensationDate) {
    return `Compensação · dia ${formatDateBr(r.compensationDate)}`;
  }
  if (r.compensationMode === 'range' && r.compensationStartDate && r.compensationEndDate) {
    return `Compensação · ${formatDateBr(r.compensationStartDate)} a ${formatDateBr(r.compensationEndDate)}`;
  }
  if (r.compensationDeadline) {
    return `Compensação · até ${formatDateBr(r.compensationDeadline)}`;
  }
  return 'Compensação';
}

type TabKey = 'ponto' | 'just' | 'banco' | 'correcao' | 'nf' | 'gestao';

const VALID_TABS: TabKey[] = ['ponto', 'just', 'banco', 'correcao', 'nf', 'gestao'];

const TC_PAGE_SIZE = 10;

const emptyPagination = <T,>(): PaginatedResponse<T> => ({
  items: [],
  total: 0,
  page: 1,
  pageSize: TC_PAGE_SIZE,
  totalPages: 1,
});

/** Estilo comum das seções (Paper) de cada aba. */
const panelSx = { p: { xs: 2, md: 3 }, border: 1, borderColor: 'divider', mt: 2 } as const;
const sectionTitleSx = { fontWeight: 800, mt: 3, mb: 1.5 } as const;
const subsectionTitleSx = { fontWeight: 700, mt: 2.5, mb: 1 } as const;
const hintSx = { color: 'text.secondary', mb: 1.5 } as const;
const tcKickerSx = {
  display: 'block',
  fontSize: '0.72rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: 'text.secondary',
} as const;

interface TimeclockPageProps {
  /** When set, the page is locked to a single section (its own route) and the
   *  section tab bar is hidden. Used e.g. for the standalone /notas-fiscais. */
  lockedTab?: TabKey;
}

const TimeclockPage: React.FC<TimeclockPageProps> = ({ lockedTab }) => {
  const { user } = useContext(AuthContext) || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const isManagerial = user?.role === 'admin' || user?.role === 'hr' || user?.role === 'manager';
  /** Administrador ou RH: listar/editar batidas de colaboradores na aba Gestão. */
  const canEditTeamPunches = user?.role === 'admin' || user?.role === 'hr';
  /** RH/admin (tudo) ou gestor (próprio + time): analisar correções de hora. */
  const canReviewTimeCorrections =
    user?.role === 'admin' || user?.role === 'hr' || user?.role === 'manager';
  /** Administrador, RH ou gestor: analisar notas fiscais (RH escopo total, gestor escopo da equipe). */
  const canReviewInvoices =
    user?.role === 'admin' || user?.role === 'hr' || user?.role === 'manager';

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const tabParam = searchParams.get('tab');
  const initialTab: TabKey =
    lockedTab ?? (VALID_TABS.includes(tabParam as TabKey) ? (tabParam as TabKey) : 'ponto');
  const [tab, setTab] = useState<TabKey>(initialTab);

  const [entries, setEntries] = useState<TimeClockEntry[]>([]);
  const [entriesPagination, setEntriesPagination] = useState<PaginatedResponse<TimeClockEntry>>(emptyPagination);
  const [days, setDays] = useState<TimeClockDay[]>([]);
  const [daysPagination, setDaysPagination] = useState<PaginatedResponse<TimeClockDay>>(emptyPagination);
  const [daysPage, setDaysPage] = useState(1);
  const [entriesPage, setEntriesPage] = useState(1);
  const [daysPageSize, setDaysPageSize] = useState(TC_PAGE_SIZE);
  const [entriesPageSize, setEntriesPageSize] = useState(TC_PAGE_SIZE);
  const [summary, setSummary] = useState<TimeclockDashboardSummary | null>(null);
  const [justifications, setJustifications] = useState<TimeJustification[]>([]);
  const [bankReqs, setBankReqs] = useState<BankHoursRequest[]>([]);
  const [timeCorrections, setTimeCorrections] = useState<TimeCorrectionRequest[]>([]);
  const [hrTimeCorrections, setHrTimeCorrections] = useState<TimeCorrectionRequest[]>([]);
  const [invoiceHistoryVersion, setInvoiceHistoryVersion] = useState(0);
  const [correctionStatusFilter, setCorrectionStatusFilter] =
    useState<ApprovalStatusFilterValue>('pending');
  const [justStatusFilter, setJustStatusFilter] =
    useState<ApprovalStatusFilterValue>('pending');
  const [bankStatusFilter, setBankStatusFilter] =
    useState<ApprovalStatusFilterValue>('pending');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [rulesHelpAnchor, setRulesHelpAnchor] = useState<HTMLElement | null>(null);
  const [recentEntriesExpanded, setRecentEntriesExpanded] = useState(false);
  const [myCorrectionsExpanded, setMyCorrectionsExpanded] = useState(false);
  const [myInvoicesExpanded, setMyInvoicesExpanded] = useState(false);
  const [myBankExpanded, setMyBankExpanded] = useState(false);
  const [gestaoCorrectionsExpanded, setGestaoCorrectionsExpanded] = useState(false);
  const [gestaoInvoicesExpanded, setGestaoInvoicesExpanded] = useState(true);
  const [gestaoJustExpanded, setGestaoJustExpanded] = useState(false);
  const [gestaoBankExpanded, setGestaoBankExpanded] = useState(false);

  const [justForm, setJustForm] = useState({
    workDate: formatWorkDateLocal(),
    type: 'falta' as TimeJustificationType,
    description: '',
  });
  const [justFile, setJustFile] = useState<File | null>(null);
  const justFileInputRef = useRef<HTMLInputElement>(null);

  const [bankForm, setBankForm] = useState({
    amount: '1',
    unit: 'hours' as BankAmountUnit,
    disposition: 'compensation' as 'compensation' | 'payment',
    compensationMode: 'single_day' as 'single_day' | 'range',
    compensationDate: '',
    compensationStartDate: '',
    compensationEndDate: '',
  });

  const [correctionForm, setCorrectionForm] = useState({
    workDate: formatWorkDateLocal(),
    kind: 'check_in' as TimeClockEntryKind,
    requestedTime: '08:00',
    applyMode: 'adjust_existing' as TimeCorrectionApplyMode,
    description: '',
  });

  const [editingHrCorrectionId, setEditingHrCorrectionId] = useState<string | null>(null);
  const [hrCorrectionEditForm, setHrCorrectionEditForm] = useState<{
    workDate: string;
    kind: TimeClockEntryKind;
    requestedTime: string;
    applyMode: TimeCorrectionApplyMode;
    description: string;
    decision: TimeCorrectionRequestStatus;
    rejectReason: string;
  } | null>(null);

  const invoicePurposeDefault =
    user?.invoiceRequired && (user.contractType === 'mei' || user.contractType === 'pj')
      ? ('salary' as const)
      : ('other' as const);
  const [invoiceForm, setInvoiceForm] = useState({
    issueDate: formatWorkDateLocal(),
    invoiceNumber: '',
    amount: '',
    notes: '',
    purpose: 'other' as 'salary' | 'other',
  });
  useEffect(() => {
    setInvoiceForm((prev) => {
      if (prev.notes.trim() || prev.amount.trim() || prev.invoiceNumber.trim()) return prev;
      if (prev.purpose === invoicePurposeDefault) return prev;
      return { ...prev, purpose: invoicePurposeDefault };
    });
  }, [invoicePurposeDefault]);
  const [invoiceFiles, setInvoiceFiles] = useState<File[]>([]);
  const invoiceFileInputRef = useRef<HTMLInputElement>(null);
  const [invoiceSubmitInProgress, setInvoiceSubmitInProgress] = useState(false);
  const [invoiceDeleteTargetId, setInvoiceDeleteTargetId] = useState<string | null>(null);
  const [invoiceDeleteInProgress, setInvoiceDeleteInProgress] = useState(false);

  const [teamMembers, setTeamMembers] = useState<Array<{ id: string; name: string }>>([]);
  const [teamUserId, setTeamUserId] = useState<string>('');
  const [teamDays, setTeamDays] = useState<TimeClockDay[]>([]);
  const [teamDaysPagination, setTeamDaysPagination] = useState<PaginatedResponse<TimeClockDay>>(emptyPagination);
  const [teamDaysPage, setTeamDaysPage] = useState(1);
  const [teamJust, setTeamJust] = useState<TimeJustification[]>([]);
  const [teamBank, setTeamBank] = useState<BankHoursRequest[]>([]);
  const [teamEntries, setTeamEntries] = useState<TimeClockEntry[]>([]);
  const [teamEntriesPagination, setTeamEntriesPagination] =
    useState<PaginatedResponse<TimeClockEntry>>(emptyPagination);
  const [teamEntriesPage, setTeamEntriesPage] = useState(1);
  const [teamDayModalEntries, setTeamDayModalEntries] = useState<TimeClockEntry[]>([]);
  const [teamEditDayEntries, setTeamEditDayEntries] = useState<TimeClockEntry[]>([]);
  const [teamTodayEntries, setTeamTodayEntries] = useState<TimeClockEntry[]>([]);
  const [teamGestaoLoading, setTeamGestaoLoading] = useState(false);
  const [editingTeamEntry, setEditingTeamEntry] = useState<TimeClockEntry | null>(null);
  const [editEntryWorkDate, setEditEntryWorkDate] = useState('');
  const [editEntryTime, setEditEntryTime] = useState('');
  const [editEntryKind, setEditEntryKind] = useState<TimeClockEntryKind>('check_in');
  const [editEntrySaving, setEditEntrySaving] = useState(false);
  const [entryDeleteTarget, setEntryDeleteTarget] = useState<TimeClockEntry | null>(null);
  const [hrCorrectionDeleteTargetId, setHrCorrectionDeleteTargetId] = useState<string | null>(null);
  const [hrCorrectionDeleteInProgress, setHrCorrectionDeleteInProgress] = useState(false);
  const [teamEntryDeleteInProgress, setTeamEntryDeleteInProgress] = useState(false);
  const [teamPunchForMemberInProgress, setTeamPunchForMemberInProgress] = useState(false);
  /** Abre modal de registro com data/hora (admin). */
  const [creatingTeamEntryKind, setCreatingTeamEntryKind] = useState<TimeClockEntryKind | null>(null);
  const [dayPunchesModalWorkDate, setDayPunchesModalWorkDate] = useState<string | null>(null);
  /** Evita que GETs antigos do useEffect sobrescrevam dados após salvar/excluir batida. */
  const teamDataFetchGen = useRef(0);
  /** Reabre o modal «Corrigir batidas» após excluir, se o usuário veio de lá. */
  const restoreDayModalAfterEditRef = useRef<string | null>(null);
  /** Elemento da aba Gestão a focar após navegar via atalho (?focus=...). */
  const pendingFocusRef = useRef<string | null>(null);
  /** Timers das tentativas de scroll até a seção em foco (fora do cleanup do efeito). */
  const focusScrollTimersRef = useRef<number[]>([]);
  const [monthlyBonusItems, setMonthlyBonusItems] = useState<MonthlyBonusRow[]>([]);
  const [monthlyBonusPagination, setMonthlyBonusPagination] =
    useState<PaginatedResponse<MonthlyBonusRow>>(emptyPagination);
  const [monthlyBonusPage, setMonthlyBonusPage] = useState(1);
  const [monthlyBonusPageSize, setMonthlyBonusPageSize] = useState(TC_PAGE_SIZE);
  const [monthlyBonusLoading, setMonthlyBonusLoading] = useState(false);

  function formatWorkDateLocal(): string {
    return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
  }

  /** Data da batida no fuso de São Paulo (YYYY-MM-DD), ignorando hora. */
  function entryWorkDateBrazil(iso: string): string {
    return new Date(iso).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
  }

  const loadMine = useCallback(async (options?: { silent?: boolean }): Promise<void> => {
    if (!user) return;
    if (!options?.silent) setLoading(true);
    try {
      const [d, e, s, j, b, tc] = await Promise.all([
        api.get<PaginatedResponse<TimeClockDay>>(
          `/timeclock/my/days?year=${year}&month=${month}&page=${daysPage}&pageSize=${daysPageSize}`,
        ),
        api.get<PaginatedResponse<TimeClockEntry>>(
          `/timeclock/my/entries?year=${year}&month=${month}&page=${entriesPage}&pageSize=${entriesPageSize}`,
        ),
        api.get<TimeclockDashboardSummary>('/timeclock/my/dashboard-summary'),
        api.get<TimeJustification[]>('/timeclock/my/justifications'),
        api.get<BankHoursRequest[]>('/timeclock/my/bank-requests'),
        api.get<TimeCorrectionRequest[]>('/timeclock/my/time-corrections'),
      ]);
      setDays(d.data.items.filter(isVisibleTimeClockDay));
      setDaysPagination(d.data);
      setEntries(e.data.items);
      setEntriesPagination(e.data);
      setSummary(s.data);
      setJustifications(j.data);
      setBankReqs(b.data);
      setTimeCorrections(tc.data);
    } catch {
      if (!options?.silent) setMsg('Não foi possível carregar os dados de ponto.');
    } finally {
      if (!options?.silent) setLoading(false);
    }
  }, [user, year, month, daysPage, entriesPage, daysPageSize, entriesPageSize]);

  useEffect(() => {
    setDaysPage(1);
    setEntriesPage(1);
    setTeamDaysPage(1);
    setTeamEntriesPage(1);
    setMonthlyBonusPage(1);
  }, [year, month]);

  const loadTeam = useCallback(async (): Promise<void> => {
    if (!isManagerial) return;
    try {
      const m = await api.get<Array<{ id: string; name: string }>>('/timeclock/team/members');
      setTeamMembers(m.data);
      const first = m.data[0]?.id ?? '';
      setTeamUserId((prev) => (prev && m.data.some((x) => x.id === prev) ? prev : first));
    } catch {
      setTeamMembers([]);
    }
  }, [isManagerial]);

  useEffect(() => {
    void loadMine();
  }, [loadMine]);

  useEffect(() => {
    if (!user) return;
    const tick = (): void => {
      if (document.visibilityState === 'visible') void loadMine({ silent: true });
    };
    const intervalId = window.setInterval(tick, 5000);
    return () => window.clearInterval(intervalId);
  }, [user, loadMine]);

  useEffect(() => {
    void loadTeam();
  }, [loadTeam]);

  const loadTeamApprovals = useCallback(async (): Promise<void> => {
    if (!isManagerial) return;
    try {
      const [justRes, bankRes, corrRes] = await Promise.all([
        api.get<TimeJustification[]>('/timeclock/team/justifications'),
        api.get<BankHoursRequest[]>('/timeclock/team/bank-requests'),
        canReviewTimeCorrections
          ? api.get<TimeCorrectionRequest[]>('/timeclock/hr/time-corrections')
          : Promise.resolve(null),
      ]);
      setTeamJust(justRes.data);
      setTeamBank(bankRes.data);
      setHrTimeCorrections(corrRes ? corrRes.data : []);
    } catch {
      setTeamJust([]);
      setTeamBank([]);
      setHrTimeCorrections([]);
    }
  }, [isManagerial, canReviewTimeCorrections]);

  const loadMonthlyBonus = useCallback(async (): Promise<void> => {
    if (!isManagerial) return;
    setMonthlyBonusLoading(true);
    try {
      const res = await api.get<PaginatedResponse<MonthlyBonusRow>>(
        `/timeclock/team/monthly-consolidated?year=${year}&month=${month}&page=${monthlyBonusPage}&pageSize=${monthlyBonusPageSize}`,
      );
      setMonthlyBonusItems(res.data.items);
      setMonthlyBonusPagination(res.data);
    } catch {
      setMonthlyBonusItems([]);
      setMonthlyBonusPagination(emptyPagination());
    } finally {
      setMonthlyBonusLoading(false);
    }
  }, [isManagerial, year, month, monthlyBonusPage, monthlyBonusPageSize]);

  useEffect(() => {
    if (tab === 'gestao') void loadTeamApprovals();
  }, [tab, loadTeamApprovals]);

  useEffect(() => {
    if (tab === 'gestao') void loadMonthlyBonus();
  }, [tab, loadMonthlyBonus]);

  const loadTeamEntriesForWorkDate = useCallback(
    async (workDate: string): Promise<TimeClockEntry[]> => {
      if (!teamUserId) return [];
      const res = await api.get<PaginatedResponse<TimeClockEntry>>(
        `/timeclock/team/entries?userId=${teamUserId}&year=${year}&month=${month}&workDate=${workDate}&page=1&pageSize=50`,
      );
      return res.data.items;
    },
    [teamUserId, year, month],
  );

  const fetchTeamMemberMonthData = useCallback(
    async (options?: { silent?: boolean }): Promise<void> => {
      if (!teamUserId || !isManagerial) return;
      teamDataFetchGen.current += 1;
      const gen = teamDataFetchGen.current;
      if (!options?.silent) setTeamGestaoLoading(true);
      try {
        const [entRes, daysRes] = await Promise.all([
          api.get<PaginatedResponse<TimeClockEntry>>(
            `/timeclock/team/entries?userId=${teamUserId}&year=${year}&month=${month}&page=${teamEntriesPage}&pageSize=${TC_PAGE_SIZE}`,
          ),
          api.get<PaginatedResponse<TimeClockDay>>(
            `/timeclock/team/days?userId=${teamUserId}&year=${year}&month=${month}&page=${teamDaysPage}&pageSize=${TC_PAGE_SIZE}`,
          ),
        ]);
        if (gen !== teamDataFetchGen.current) return;
        setTeamEntries(entRes.data.items);
        setTeamEntriesPagination(entRes.data);
        setTeamDays(daysRes.data.items.filter(isVisibleTimeClockDay));
        setTeamDaysPagination(daysRes.data);
      } catch (err: unknown) {
        if (gen !== teamDataFetchGen.current) return;
        setTeamEntries([]);
        setTeamEntriesPagination(emptyPagination());
        setTeamDays([]);
        setTeamDaysPagination(emptyPagination());
        if (!options?.silent) {
          const m =
            (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
          setMsg(
            Array.isArray(m)
              ? m.join(', ')
              : m || 'Sem permissão para listar as batidas do colaborador ou erro no servidor.',
          );
        }
      } finally {
        if (gen === teamDataFetchGen.current && !options?.silent) setTeamGestaoLoading(false);
      }
    },
    [teamUserId, year, month, teamEntriesPage, teamDaysPage, isManagerial],
  );

  const reloadTeamEntriesAndDays = useCallback(
    async (options?: { full?: boolean; silent?: boolean }): Promise<void> => {
      await fetchTeamMemberMonthData({ silent: options?.silent });
      if (options?.full) {
        await loadTeamApprovals();
        await loadMine({ silent: true });
      }
    },
    [fetchTeamMemberMonthData, loadTeamApprovals, loadMine],
  );

  useEffect(() => {
    setTeamDaysPage(1);
    setTeamEntriesPage(1);
  }, [teamUserId]);

  useEffect(() => {
    if (tab !== 'gestao' || !teamUserId || !isManagerial) {
      setTeamEntries([]);
      setTeamEntriesPagination(emptyPagination());
      setTeamDays([]);
      setTeamDaysPagination(emptyPagination());
      setTeamGestaoLoading(false);
      return;
    }
    void fetchTeamMemberMonthData();
  }, [tab, teamUserId, year, month, teamEntriesPage, teamDaysPage, isManagerial, fetchTeamMemberMonthData]);

  useEffect(() => {
    if (!teamUserId || tab !== 'gestao') {
      setTeamTodayEntries([]);
      return;
    }
    void loadTeamEntriesForWorkDate(formatWorkDateLocal()).then(setTeamTodayEntries);
  }, [teamUserId, tab, loadTeamEntriesForWorkDate]);

  useEffect(() => {
    if (!dayPunchesModalWorkDate || !teamUserId) {
      setTeamDayModalEntries([]);
      return;
    }
    void loadTeamEntriesForWorkDate(dayPunchesModalWorkDate).then(setTeamDayModalEntries);
  }, [dayPunchesModalWorkDate, teamUserId, loadTeamEntriesForWorkDate]);

  useEffect(() => {
    if (!teamUserId || (!editingTeamEntry && !creatingTeamEntryKind)) {
      setTeamEditDayEntries([]);
      return;
    }
    const workDate =
      editEntryWorkDate ||
      (editingTeamEntry ? isoToBrazilDateAndTime(editingTeamEntry.occurredAt).workDate : formatWorkDateLocal());
    void loadTeamEntriesForWorkDate(workDate).then(setTeamEditDayEntries);
  }, [
    teamUserId,
    editingTeamEntry,
    creatingTeamEntryKind,
    editEntryWorkDate,
    loadTeamEntriesForWorkDate,
  ]);

  useEffect(() => {
    if (tab !== 'gestao' || !teamUserId || !isManagerial) return;
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') void reloadTeamEntriesAndDays({ silent: true });
    }, 5000);
    return () => window.clearInterval(intervalId);
  }, [tab, teamUserId, isManagerial, reloadTeamEntriesAndDays]);

  const setActiveTab = useCallback(
    (next: TabKey): void => {
      setTab(next);
      if (next === 'ponto') {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('tab');
        setSearchParams(nextParams, { replace: true });
      } else {
        setSearchParams({ tab: next }, { replace: true });
      }
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    if (lockedTab) {
      setTab(lockedTab);
      return;
    }
    const p = searchParams.get('tab');
    if (p && VALID_TABS.includes(p as TabKey)) {
      setTab(p as TabKey);
    }
  }, [searchParams, lockedTab]);

  useEffect(() => {
    if (window.location.hash !== '#tc-punch') return;
    setActiveTab('ponto');
    const id = window.setTimeout(() => {
      document.getElementById('tc-punch')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
    return () => window.clearTimeout(id);
  }, [setActiveTab]);

  /** Rola até o elemento marcado como foco pendente, se já estiver renderizado. */
  const scrollToPendingFocus = useCallback((clearAfter = false): void => {
    const targetId = pendingFocusRef.current;
    if (!targetId) return;
    const el = document.getElementById(targetId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (clearAfter) pendingFocusRef.current = null;
  }, []);

  /**
   * Agenda várias tentativas de scroll: a seção-alvo fica no fim da aba Gestão e
   * só assenta após o carregamento assíncrono dos dados da equipe. Os timers ficam
   * num ref (não no cleanup do efeito) para não serem cancelados quando o efeito
   * re-executa ao limpar o parâmetro ?focus da URL.
   */
  const scheduleFocusScroll = useCallback((): void => {
    focusScrollTimersRef.current.forEach((t) => window.clearTimeout(t));
    const delays = [150, 450, 800, 1300];
    focusScrollTimersRef.current = delays.map((ms, i) =>
      window.setTimeout(() => scrollToPendingFocus(i === delays.length - 1), ms),
    );
  }, [scrollToPendingFocus]);

  useEffect(() => () => focusScrollTimersRef.current.forEach((t) => window.clearTimeout(t)), []);

  useEffect(() => {
    const focus = searchParams.get('focus');
    if (focus === 'correcoes' && canReviewTimeCorrections) {
      pendingFocusRef.current = 'tc-hr-corrections';
      setGestaoCorrectionsExpanded(true);
    } else if (focus === 'notas-fiscais' && canReviewInvoices) {
      pendingFocusRef.current = 'tc-hr-invoices';
      setGestaoInvoicesExpanded(true);
    } else {
      return;
    }
    setActiveTab('gestao');
    scheduleFocusScroll();
  }, [searchParams, canReviewTimeCorrections, canReviewInvoices, setActiveTab, scheduleFocusScroll]);

  const todayPunchState = useMemo(() => {
    const todayWorkDate = formatWorkDateLocal();
    const tp = summary?.todayPunches;
    const todayDay = summary?.recentDays.find((d) => d.workDate === todayWorkDate);
    const hasCheckIn = !!(tp?.checkInAt ?? todayDay?.firstCheckInAt);
    const hasPrimaryCheckOut = !!tp?.checkOutAt;
    const hasCompletedPrimary =
      tp?.hasCompletedPrimary ?? !!(hasCheckIn && hasPrimaryCheckOut);
    const sessionOpen = tp?.sessionOpen ?? (hasCheckIn && !hasCompletedPrimary);
    const canReopen = tp?.canReopen ?? (hasCompletedPrimary && !sessionOpen);
    return {
      hasCheckIn,
      hasCheckOut: hasPrimaryCheckOut,
      checkInAt: tp?.checkInAt ?? todayDay?.firstCheckInAt ?? null,
      checkOutAt: tp?.checkOutAt ?? null,
      hasLunchOut: !!tp?.lunchOutAt,
      hasLunchIn: !!tp?.lunchInAt,
      lunchOutAt: tp?.lunchOutAt ?? null,
      lunchInAt: tp?.lunchInAt ?? null,
      sessionOpen,
      hasCompletedPrimary,
      canReopen,
      awaitingLunchReturn:
        !!tp?.lunchOutAt && !tp?.lunchInAt && !hasCompletedPrimary && sessionOpen,
    };
  }, [summary]);

  /** Contador regressivo só na UI (não altera saldo/banco persistido). */
  const [liveNow, setLiveNow] = useState(() => Date.now());
  useEffect(() => {
    if (!todayPunchState.sessionOpen || !todayPunchState.checkInAt || todayPunchState.hasCheckOut) {
      return;
    }
    const id = window.setInterval(() => setLiveNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, [todayPunchState.sessionOpen, todayPunchState.checkInAt, todayPunchState.hasCheckOut]);

  const todayLiveBalanceMinutes = useMemo(() => {
    if (!todayPunchState.sessionOpen || !todayPunchState.checkInAt || todayPunchState.hasCheckOut) {
      return null;
    }
    return computeLiveDayBalanceMinutes({
      checkInAt: todayPunchState.checkInAt,
      checkOutAt: todayPunchState.checkOutAt,
      lunchOutAt: todayPunchState.lunchOutAt,
      lunchInAt: todayPunchState.lunchInAt,
      now: new Date(liveNow),
    });
  }, [todayPunchState, liveNow]);

  const punch = async (kind: TimeClockEntryKind): Promise<void> => {
    try {
      await api.post('/timeclock/punch', { kind });
      await loadMine();
    } catch {
      // silent — UI state updates via loadMine
    }
  };

  const submitJustification = async (): Promise<void> => {
    try {
      const res = await api.post<TimeJustification>('/timeclock/my/justifications', justForm);
      if (justFile) {
        const fd = new FormData();
        fd.append('file', justFile);
        await api.post(`/timeclock/my/justifications/${res.data.id}/files`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      setMsg('Justificativa enviada.');
      setJustForm({ workDate: formatWorkDateLocal(), type: 'falta', description: '' });
      setJustFile(null);
      if (justFileInputRef.current) {
        justFileInputRef.current.value = '';
      }
      await loadMine();
    } catch {
      setMsg('Erro ao enviar justificativa.');
    }
  };

  const submitBank = async (): Promise<void> => {
    const minutes = bankAmountToMinutes(parseBankAmount(bankForm.amount), bankForm.unit);
    if (minutes < 1) {
      setMsg('Informe uma quantidade válida maior que zero.');
      return;
    }
    if (bankForm.disposition === 'compensation') {
      if (bankForm.compensationMode === 'single_day') {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(bankForm.compensationDate)) {
          setMsg('Informe o dia de compensação.');
          return;
        }
      } else if (
        !/^\d{4}-\d{2}-\d{2}$/.test(bankForm.compensationStartDate) ||
        !/^\d{4}-\d{2}-\d{2}$/.test(bankForm.compensationEndDate)
      ) {
        setMsg('Informe o início e o fim do período de compensação.');
        return;
      } else if (bankForm.compensationEndDate < bankForm.compensationStartDate) {
        setMsg('A data final deve ser igual ou posterior à data inicial.');
        return;
      }
    }
    try {
      await api.post('/timeclock/my/bank-requests', {
        minutes,
        disposition: bankForm.disposition,
        ...(bankForm.disposition === 'compensation'
          ? bankForm.compensationMode === 'single_day'
            ? {
                compensationMode: 'single_day',
                compensationDate: bankForm.compensationDate,
              }
            : {
                compensationMode: 'range',
                compensationStartDate: bankForm.compensationStartDate,
                compensationEndDate: bankForm.compensationEndDate,
              }
          : {}),
      });
      setMsg('Solicitação de banco de horas criada.');
      setBankForm((p) => ({
        ...p,
        amount: '1',
        unit: 'hours',
        compensationMode: 'single_day',
        compensationDate: '',
        compensationStartDate: '',
        compensationEndDate: '',
      }));
      await loadMine();
    } catch (err: unknown) {
      const m =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setMsg(m || 'Erro na solicitação.');
    }
  };

  const submitTimeCorrection = async (): Promise<void> => {
    try {
      await api.post('/timeclock/my/time-corrections', correctionForm);
      setMsg('Solicitação de correção de horário enviada ao RH.');
      setCorrectionForm({
        workDate: formatWorkDateLocal(),
        kind: 'check_in',
        requestedTime: '08:00',
        applyMode: 'adjust_existing',
        description: '',
      });
      await loadMine();
    } catch (err: unknown) {
      const m =
        (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      setMsg(Array.isArray(m) ? m.join(', ') : m || 'Erro ao enviar solicitação de correção.');
    }
  };

  const submitInvoice = async (): Promise<void> => {
    if (!invoiceFiles.length) {
      setMsg('Selecione ao menos um arquivo da nota fiscal.');
      return;
    }
    if (invoiceFiles.length > 10) {
      setMsg('Envie no máximo 10 arquivos por nota fiscal.');
      return;
    }
    if (!invoiceForm.notes.trim()) {
      setMsg('Informe a observação.');
      return;
    }
    const amountText = invoiceForm.amount.trim();
    const amountCents = amountText ? reaisInputToCents(amountText) : null;
    if (amountText && amountCents == null) {
      setMsg('Valor inválido. Use reais, como 1500,00.');
      return;
    }
    if (invoiceForm.purpose === 'salary' && (amountCents == null || amountCents <= 0)) {
      setMsg('Informe o valor da nota de salário. Esse valor será lançado na folha após a aprovação.');
      return;
    }
    setInvoiceSubmitInProgress(true);
    try {
      const fd = new FormData();
      fd.append('referenceYear', String(year));
      fd.append('referenceMonth', String(month));
      fd.append('issueDate', invoiceForm.issueDate);
      const invoiceNumber = invoiceForm.invoiceNumber.trim();
      if (invoiceNumber) fd.append('invoiceNumber', invoiceNumber);
      fd.append('notes', invoiceForm.notes.trim());
      fd.append('purpose', invoiceForm.purpose);
      if (amountCents != null) fd.append('amountCents', String(amountCents));
      for (const file of invoiceFiles) {
        fd.append('files', file);
      }
      await api.post('/timeclock/my/invoices', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMsg(
        invoiceForm.purpose === 'salary'
          ? 'Nota de salário enviada ao RH. Após a aprovação, o valor entra na folha do mês.'
          : 'Nota fiscal enviada ao RH para análise.',
      );
      setInvoiceHistoryVersion((version) => version + 1);
      setInvoiceForm({
        issueDate: formatWorkDateLocal(),
        invoiceNumber: '',
        amount: '',
        notes: '',
        purpose: invoicePurposeDefault,
      });
      setInvoiceFiles([]);
      if (invoiceFileInputRef.current) invoiceFileInputRef.current.value = '';
      await loadMine();
    } catch (err: unknown) {
      const m =
        (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      setMsg(Array.isArray(m) ? m.join(', ') : m || 'Erro ao enviar nota fiscal.');
    } finally {
      setInvoiceSubmitInProgress(false);
    }
  };

  const reviewInvoice = async (id: string, decision: 'approved' | 'rejected'): Promise<void> => {
    const rejectReason =
      decision === 'rejected' ? window.prompt('Motivo da rejeição (opcional):') || undefined : undefined;
    try {
      await api.patch(`/timeclock/hr/invoices/${id}`, { decision, rejectReason });
      setMsg(decision === 'approved' ? 'Nota fiscal aprovada.' : 'Nota fiscal rejeitada.');
      setInvoiceHistoryVersion((version) => version + 1);
      await loadTeamApprovals();
      await loadMine({ silent: true });
    } catch (err: unknown) {
      const m =
        (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      setMsg(Array.isArray(m) ? m.join(', ') : m || 'Erro ao analisar nota fiscal.');
    }
  };

  const requestDeleteInvoice = (id: string): void => {
    setInvoiceDeleteTargetId(id);
  };

  const cancelDeleteInvoice = (): void => {
    if (invoiceDeleteInProgress) return;
    setInvoiceDeleteTargetId(null);
  };

  const confirmDeleteInvoice = async (): Promise<void> => {
    if (!invoiceDeleteTargetId) return;
    setInvoiceDeleteInProgress(true);
    try {
      await api.delete(`/timeclock/hr/invoices/${invoiceDeleteTargetId}`);
      setMsg('Nota fiscal excluída.');
      setInvoiceDeleteTargetId(null);
      setInvoiceHistoryVersion((version) => version + 1);
      await loadTeamApprovals();
      await loadMine({ silent: true });
    } catch (err: unknown) {
      const m =
        (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      setMsg(Array.isArray(m) ? m.join(', ') : m || 'Erro ao excluir nota fiscal.');
    } finally {
      setInvoiceDeleteInProgress(false);
    }
  };

  const reviewTimeCorrection = async (id: string, decision: 'approved' | 'rejected'): Promise<void> => {
    const rejectReason =
      decision === 'rejected' ? window.prompt('Motivo da rejeição (opcional):') || undefined : undefined;
    try {
      await api.patch(`/timeclock/hr/time-corrections/${id}`, { decision, rejectReason });
      setMsg(decision === 'approved' ? 'Correção aplicada ao ponto.' : 'Solicitação rejeitada.');
      await loadTeamApprovals();
      await loadMine({ silent: true });
      if (teamUserId) await fetchTeamMemberMonthData({ silent: true });
    } catch (err: unknown) {
      const m =
        (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      setMsg(Array.isArray(m) ? m.join(', ') : m || 'Erro ao analisar solicitação.');
    }
  };

  const openHrCorrectionEdit = (request: TimeCorrectionRequest): void => {
    setEditingHrCorrectionId(request.id);
    setHrCorrectionEditForm({
      workDate: request.workDate,
      kind: request.kind,
      requestedTime: request.requestedTime,
      applyMode: request.applyMode ?? 'adjust_existing',
      description: request.description,
      decision: request.status,
      rejectReason: request.rejectReason || '',
    });
  };

  const cancelHrCorrectionEdit = (): void => {
    setEditingHrCorrectionId(null);
    setHrCorrectionEditForm(null);
  };

  const saveHrCorrectionEdit = async (): Promise<void> => {
    if (!editingHrCorrectionId || !hrCorrectionEditForm) return;
    const payload: {
      workDate: string;
      kind: TimeClockEntryKind;
      requestedTime: string;
      applyMode: TimeCorrectionApplyMode;
      description: string;
      decision?: 'approved' | 'rejected';
      rejectReason?: string;
    } = {
      workDate: hrCorrectionEditForm.workDate,
      kind: hrCorrectionEditForm.kind,
      requestedTime: hrCorrectionEditForm.requestedTime,
      applyMode: hrCorrectionEditForm.applyMode,
      description: hrCorrectionEditForm.description,
    };
    if (hrCorrectionEditForm.decision !== 'pending') {
      payload.decision = hrCorrectionEditForm.decision;
      if (hrCorrectionEditForm.decision === 'rejected') {
        payload.rejectReason = hrCorrectionEditForm.rejectReason;
      }
    }
    try {
      await api.patch(`/timeclock/hr/time-corrections/${editingHrCorrectionId}`, payload);
      setMsg('Solicitação de correção atualizada.');
      cancelHrCorrectionEdit();
      await loadTeamApprovals();
      await loadMine({ silent: true });
      if (teamUserId) await fetchTeamMemberMonthData({ silent: true });
    } catch (err: unknown) {
      const m =
        (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      setMsg(Array.isArray(m) ? m.join(', ') : m || 'Erro ao atualizar solicitação.');
    }
  };

  const requestDeleteHrCorrection = (id: string): void => {
    setHrCorrectionDeleteTargetId(id);
  };

  const cancelDeleteHrCorrection = (): void => {
    if (hrCorrectionDeleteInProgress) return;
    setHrCorrectionDeleteTargetId(null);
  };

  const confirmDeleteHrCorrection = async (): Promise<void> => {
    if (!hrCorrectionDeleteTargetId) return;
    const id = hrCorrectionDeleteTargetId;
    setHrCorrectionDeleteInProgress(true);
    try {
      await api.delete(`/timeclock/hr/time-corrections/${id}`);
      if (editingHrCorrectionId === id) cancelHrCorrectionEdit();
      setMsg('Solicitação de correção excluída.');
      setHrCorrectionDeleteTargetId(null);
      await loadTeamApprovals();
      await loadMine({ silent: true });
      if (teamUserId) await fetchTeamMemberMonthData({ silent: true });
    } catch (err: unknown) {
      const m =
        (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      setMsg(Array.isArray(m) ? m.join(', ') : m || 'Erro ao excluir solicitação.');
    } finally {
      setHrCorrectionDeleteInProgress(false);
    }
  };

  const reviewJust = async (id: string, decision: 'approved' | 'rejected'): Promise<void> => {
    const rejectReason =
      decision === 'rejected' ? window.prompt('Motivo da rejeição (opcional):') || undefined : undefined;
    try {
      await api.patch(`/timeclock/team/justifications/${id}`, { decision, rejectReason });
      await loadTeamApprovals();
    } catch {
      setMsg('Erro ao atualizar justificativa.');
    }
  };

  const reviewBank = async (id: string, decision: 'approved' | 'rejected'): Promise<void> => {
    const managerComment =
      decision === 'rejected' ? window.prompt('Comentário (opcional):') || undefined : undefined;
    try {
      await api.patch(`/timeclock/team/bank-requests/${id}`, { decision, managerComment });
      await loadTeamApprovals();
      await loadMine();
    } catch {
      setMsg('Erro ao atualizar solicitação.');
    }
  };

  const openAdminEditEntry = (entry: TimeClockEntry): void => {
    setCreatingTeamEntryKind(null);
    if (dayPunchesModalWorkDate) {
      restoreDayModalAfterEditRef.current = dayPunchesModalWorkDate;
    }
    setDayPunchesModalWorkDate(null);
    const { workDate, timeHM } = isoToBrazilDateAndTime(entry.occurredAt);
    setEditingTeamEntry(entry);
    setEditEntryWorkDate(workDate);
    setEditEntryTime(timeHM);
    setEditEntryKind(entry.kind);
  };

  const closeAdminEditEntry = (): void => {
    if (editEntrySaving || teamEntryDeleteInProgress || teamPunchForMemberInProgress) return;
    restoreDayModalAfterEditRef.current = null;
    setEditingTeamEntry(null);
  };

  const closeCreateTeamEntry = (): void => {
    if (teamPunchForMemberInProgress) return;
    setCreatingTeamEntryKind(null);
  };

  const openCreateTeamEntry = (kind: TimeClockEntryKind): void => {
    if (!teamUserId) return;
    setEditingTeamEntry(null);
    const now = new Date();
    const { workDate, timeHM } = isoToBrazilDateAndTime(now.toISOString());
    setCreatingTeamEntryKind(kind);
    setEditEntryWorkDate(workDate);
    setEditEntryTime(timeHM);
    setEditEntryKind(kind);
  };

  const punchStateForWorkDate = (
    workDate: string,
    sourceEntries: TimeClockEntry[] = teamEditDayEntries,
  ): { hasCheckIn: boolean; hasCheckOut: boolean } => {
    const dayEntries = sourceEntries.filter((e) => entryWorkDateBrazil(e.occurredAt) === workDate);
    const dayRow = teamDays.find((d) => d.workDate === workDate);
    const checkIn = dayEntries.find((e) => e.kind === 'check_in');
    const checkOut = dayEntries.find((e) => e.kind === 'check_out');
    return {
      hasCheckIn: !!checkIn || !!dayRow?.firstCheckInAt,
      hasCheckOut: !!checkOut || !!dayRow?.lastCheckOutAt,
    };
  };

  /** Ao alternar Entrada/Saída no editor, carrega a outra batida do mesmo dia (data e hora). */
  const handleAdminEntryKindChange = (newKind: TimeClockEntryKind): void => {
    if (creatingTeamEntryKind) {
      setEditEntryKind(newKind);
      return;
    }
    if (!editingTeamEntry) return;

    const workDate = editEntryWorkDate;
    const peer = teamEditDayEntries.find(
      (e) => e.kind === newKind && entryWorkDateBrazil(e.occurredAt) === workDate,
    );

    if (peer) {
      const br = isoToBrazilDateAndTime(peer.occurredAt);
      setEditingTeamEntry(peer);
      setEditEntryWorkDate(br.workDate);
      setEditEntryTime(br.timeHM);
      setEditEntryKind(peer.kind);
      return;
    }

    setEditEntryKind(newKind);
  };

  /** Entrada só pode ser excluída depois que a saída do mesmo dia for removida. */
  const canDeleteEditingTeamEntry = useMemo(() => {
    if (!editingTeamEntry) return false;
    if (editingTeamEntry.kind === 'check_out') return true;
    const workDate = isoToBrazilDateAndTime(editingTeamEntry.occurredAt).workDate;
    const hasOtherCheckOut = teamEditDayEntries.some(
      (e) =>
        e.id !== editingTeamEntry.id &&
        e.kind === 'check_out' &&
        entryWorkDateBrazil(e.occurredAt) === workDate,
    );
    return !hasOtherCheckOut;
  }, [editingTeamEntry, teamEditDayEntries]);

  const requestDeleteTeamEntry = (entry: TimeClockEntry): void => {
    if (entry.kind === 'check_in') {
      const workDate = isoToBrazilDateAndTime(entry.occurredAt).workDate;
      if (punchStateForWorkDate(workDate, teamDayModalEntries).hasCheckOut) {
        setMsg('Exclua a saída deste dia antes de excluir a entrada.');
        return;
      }
    }
    setEntryDeleteTarget(entry);
  };

  const cancelDeleteTeamEntry = (): void => {
    if (teamEntryDeleteInProgress) return;
    setEntryDeleteTarget(null);
  };

  const confirmDeleteTeamEntry = async (): Promise<void> => {
    if (!entryDeleteTarget) return;
    const deleted = entryDeleteTarget;
    setTeamEntryDeleteInProgress(true);
    try {
      await api.delete(`/timeclock/team/entries/${deleted.id}`);
      const deletedWorkDate = isoToBrazilDateAndTime(deleted.occurredAt).workDate;
      const modalWorkDate = dayPunchesModalWorkDate;
      setMsg('Batida excluída.');
      setEntryDeleteTarget(null);
      setEditingTeamEntry(null);
      setCreatingTeamEntryKind(null);
      restoreDayModalAfterEditRef.current = null;
      setDayPunchesModalWorkDate(null);
      await reloadTeamEntriesAndDays({ full: true });
      if (modalWorkDate) {
        void loadTeamEntriesForWorkDate(modalWorkDate).then(setTeamDayModalEntries);
      } else {
        void loadTeamEntriesForWorkDate(deletedWorkDate).then(setTeamEditDayEntries);
      }
    } catch (err: unknown) {
      const m =
        (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      setMsg(Array.isArray(m) ? m.join(', ') : m || 'Erro ao excluir o registro.');
    } finally {
      setTeamEntryDeleteInProgress(false);
    }
  };

  const saveAdminTeamEntry = async (): Promise<void> => {
    if (!editingTeamEntry || !teamUserId || teamEntryDeleteInProgress) return;
    setEditEntrySaving(true);
    try {
      const occurredAt = brazilDateTimeToIso(editEntryWorkDate, editEntryTime);
      await api.patch(`/timeclock/team/entries/${editingTeamEntry.id}`, {
        occurredAt,
        kind: editEntryKind,
      });
      setMsg('Registro de ponto atualizado.');
      setEditingTeamEntry(null);
      await reloadTeamEntriesAndDays({ full: true });
      const restoreDayAfterSave = restoreDayModalAfterEditRef.current;
      restoreDayModalAfterEditRef.current = null;
      if (restoreDayAfterSave) setDayPunchesModalWorkDate(restoreDayAfterSave);
    } catch (err: unknown) {
      const m =
        (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      setMsg(Array.isArray(m) ? m.join(', ') : m || 'Erro ao atualizar o registro.');
    } finally {
      setEditEntrySaving(false);
    }
  };

  const memberNameMap = useMemo(
    () => Object.fromEntries(teamMembers.map((m) => [m.id, m.name])),
    [teamMembers],
  );

  const correctionStatusCounts = useMemo(
    () => countByApprovalFilter(hrTimeCorrections, matchesSimpleApprovalStatus),
    [hrTimeCorrections],
  );
  const filteredHrTimeCorrections = useMemo(
    () =>
      hrTimeCorrections.filter((r) =>
        matchesSimpleApprovalStatus(r.status, correctionStatusFilter),
      ),
    [hrTimeCorrections, correctionStatusFilter],
  );

  const justStatusCounts = useMemo(
    () => countByApprovalFilter(teamJust, matchesJustificationStatus),
    [teamJust],
  );
  const filteredTeamJust = useMemo(
    () => teamJust.filter((j) => matchesJustificationStatus(j.status, justStatusFilter)),
    [teamJust, justStatusFilter],
  );

  const bankStatusCounts = useMemo(
    () => countByApprovalFilter(teamBank, matchesSimpleApprovalStatus),
    [teamBank],
  );
  const filteredTeamBank = useMemo(
    () => teamBank.filter((r) => matchesSimpleApprovalStatus(r.status, bankStatusFilter)),
    [teamBank, bankStatusFilter],
  );

  const teamMemberSelectOptions = useMemo(
    () => teamMembers.map((m) => ({ value: m.id, label: m.name })),
    [teamMembers],
  );

  const justificationTypeOptions = useMemo(
    () => JUST_TYPES.map((t) => ({ value: t.value, label: t.label })),
    [],
  );

  const teamEntriesForDayModal = useMemo(() => teamDayModalEntries, [teamDayModalEntries]);

  /** Estado das batidas de hoje do colaborador selecionado (habilita/desabilita os botões rápidos). */
  const selectedMemberTodayPunch = useMemo(() => {
    const checkIn = teamTodayEntries.find((e) => e.kind === 'check_in');
    const checkOut = teamTodayEntries.find((e) => e.kind === 'check_out');
    return {
      hasCheckIn: !!checkIn,
      hasCheckOut: !!checkOut,
    };
  }, [teamTodayEntries]);

  const saveCreateTeamEntry = async (): Promise<void> => {
    if (!teamUserId || !creatingTeamEntryKind || teamPunchForMemberInProgress) return;
    if (!editEntryWorkDate || !editEntryTime) {
      setMsg('Informe a data e a hora da batida.');
      return;
    }
    const dayState = punchStateForWorkDate(editEntryWorkDate);
    if (editEntryKind === 'check_in' && dayState.hasCheckIn) {
      setMsg('Já existe entrada registrada neste dia para o colaborador.');
      return;
    }
    if (editEntryKind === 'check_out' && !dayState.hasCheckIn) {
      setMsg('Registre a entrada neste dia antes da saída.');
      return;
    }
    if (editEntryKind === 'check_out' && dayState.hasCheckOut) {
      setMsg('Já existe saída registrada neste dia para o colaborador.');
      return;
    }
    setTeamPunchForMemberInProgress(true);
    try {
      const occurredAt = brazilDateTimeToIso(editEntryWorkDate, editEntryTime);
      await api.post('/timeclock/team/entries', {
        userId: teamUserId,
        kind: editEntryKind,
        occurredAt,
      });
      setMsg(
        editEntryKind === 'check_in'
          ? 'Entrada registrada para o colaborador.'
          : 'Saída registrada para o colaborador.',
      );
      setCreatingTeamEntryKind(null);
      await reloadTeamEntriesAndDays({ full: true });
    } catch (err: unknown) {
      const m =
        (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      setMsg(Array.isArray(m) ? m.join(', ') : m || 'Erro ao registrar o ponto do colaborador.');
    } finally {
      setTeamPunchForMemberInProgress(false);
    }
  };

  if (searchParams.get('tab') === 'relatorios') {
    return <Navigate to="/reports?section=timeclock" replace />;
  }

  const bankPreviewMinutes = bankAmountToMinutes(parseBankAmount(bankForm.amount), bankForm.unit);
  const rulesHelpOpen = Boolean(rulesHelpAnchor);

  const rulesHelpButton = (
    <>
      <IconButton
        aria-label="Regras do ponto eletrônico"
        aria-expanded={rulesHelpOpen}
        onClick={(e) => setRulesHelpAnchor(rulesHelpAnchor ? null : e.currentTarget)}
        color="primary"
      >
        <HelpOutlineIcon />
      </IconButton>
      <Popover
        open={rulesHelpOpen}
        anchorEl={rulesHelpAnchor}
        onClose={() => setRulesHelpAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { p: 0, width: 340, maxWidth: '92vw', border: 1, borderColor: 'divider' } } }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.5, bgcolor: 'action.hover' }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: 1,
              '& svg': { fontSize: 20 },
            }}
          >
            <HelpOutlineIcon />
          </Box>
          <Typography sx={{ fontWeight: 800 }}>Regras do ponto</Typography>
        </Box>

        <Box sx={{ p: 2 }}>
          {/* Jornadas válidas → chips */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <ScheduleOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>
              Jornadas válidas
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {['08:00–17:00', '08:30–17:30', '09:00–18:00'].map((j) => (
              <Chip
                key={j}
                label={j}
                size="small"
                variant="outlined"
                sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, borderColor: 'divider' }}
              />
            ))}
          </Stack>

          <Divider sx={{ mb: 2 }} />

          {/* Rule rows: icon badge + label + text */}
          <Stack spacing={2}>
            {[
              {
                icon: <CalculateOutlinedIcon />,
                title: 'Saldo e hora extra',
                text: 'Desconta-se 1 h de almoço do intervalo entre a primeira entrada e a última saída (meta líquida 8 h).',
              },
              {
                icon: <AutoModeOutlinedIcon />,
                title: 'Cálculo automático',
                text: 'Atrasos, faltas, saídas antecipadas e horas extras são calculados automaticamente.',
              },
            ].map((rule) => (
              <Box key={rule.title} sx={{ display: 'flex', gap: 1.25 }}>
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 30,
                    height: 30,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'action.hover',
                    color: 'primary.main',
                    borderRadius: 1,
                    '& svg': { fontSize: 18 },
                  }}
                >
                  {rule.icon}
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {rule.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {rule.text}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      </Popover>
    </>
  );

  const nfMode = lockedTab === 'nf';

  return (
    <PageContainer
      title={nfMode ? 'Notas fiscais' : 'Ponto eletrônico e banco de horas'}
      subtitle={nfMode ? 'Envie e acompanhe suas notas fiscais do mês.' : 'Registre seu ponto, acompanhe o banco de horas, justificativas e correções.'}
      maxWidth="xl"
      icon={nfMode ? <ReceiptLongOutlinedIcon /> : <AccessTimeOutlinedIcon />}
      actions={nfMode ? undefined : rulesHelpButton}
    >
      {msg ? (
        <Alert severity="info" onClose={() => setMsg(null)} sx={{ mb: 2 }}>
          {msg}
        </Alert>
      ) : null}

      {lockedTab ? null : (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tab}
            onChange={(_e, value: TabKey) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label="Seções"
          >
            <Tab value="ponto" label="Meu ponto" icon={<AccessTimeOutlinedIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
            <Tab value="just" label="Justificativas" icon={<EventBusyOutlinedIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
            <Tab value="banco" label="Banco de horas" icon={<SavingsOutlinedIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
            <Tab value="correcao" label="Correção de hora" icon={<EditCalendarOutlinedIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
            {isManagerial ? (
              <Tab value="gestao" label="Gestão" icon={<SupervisorAccountOutlinedIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
            ) : null}
          </Tabs>
        </Box>
      )}

      {tab === 'ponto' ? (
        <Paper sx={panelSx}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'flex-start' } }}
          >
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <Box sx={{ minWidth: 120 }}>
                <PremiumSelect
                  id="tc-month-select"
                  label="Mês"
                  value={String(month)}
                  options={MONTH_SELECT_OPTIONS}
                  onChange={(v) => setMonth(Number(v))}
                />
              </Box>
              <TextField
                label="Ano"
                type="number"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                slotProps={{ htmlInput: { min: 2020, max: 2040 } }}
                sx={{ width: 110 }}
              />
            </Stack>

            <Box id="tc-punch" sx={{ flex: 1, minWidth: 0 }}>
              <Stack
                direction="row"
                spacing={1}
                sx={{ justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', mb: 1 }}
              >
                <Typography component="strong" sx={{ fontWeight: 800 }}>
                  Registro do dia
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {todayPunchState.canReopen
                    ? 'Jornada encerrada — reabra para hora extra (almoço bloqueado)'
                    : todayPunchState.hasCompletedPrimary && todayPunchState.sessionOpen
                      ? 'Expediente reaberto (hora extra)'
                      : 'Ordem: entrada → ir ao almoço → volta → saída'}
                </Typography>
              </Stack>
              {(() => {
                const s = todayPunchState;
                const steps: Array<{
                  kind: PunchKind;
                  title: string;
                  time: string | null;
                  done: boolean;
                  enabled: boolean;
                  tooltip: string;
                }> = [
                  {
                    kind: 'check_in',
                    title: s.canReopen ? 'Reabrir' : 'Entrada',
                    time: s.checkInAt ? formatTime(s.checkInAt) : null,
                    done: s.hasCheckIn && s.sessionOpen && !s.hasCompletedPrimary,
                    enabled: !loading && !s.sessionOpen,
                    tooltip: s.sessionOpen
                      ? 'Já há um expediente aberto'
                      : s.canReopen
                        ? 'Reabrir expediente (hora extra)'
                        : 'Registrar entrada',
                  },
                  {
                    kind: 'lunch_out',
                    title: 'Ir ao almoço',
                    time: s.lunchOutAt ? formatTime(s.lunchOutAt) : null,
                    done: s.hasLunchOut,
                    enabled:
                      !loading && s.sessionOpen && !s.hasCompletedPrimary && !s.hasLunchOut,
                    tooltip: s.hasCompletedPrimary
                      ? 'Almoço bloqueado após a primeira saída'
                      : s.hasLunchOut
                        ? `Saída almoço às ${formatTime(s.lunchOutAt)}`
                        : !s.sessionOpen
                          ? 'Registre a entrada antes do almoço'
                          : 'Registrar saída para o almoço',
                  },
                  {
                    kind: 'lunch_in',
                    title: 'Volta do almoço',
                    time: s.lunchInAt ? formatTime(s.lunchInAt) : null,
                    done: s.hasLunchIn,
                    enabled:
                      !loading &&
                      s.sessionOpen &&
                      !s.hasCompletedPrimary &&
                      s.hasLunchOut &&
                      !s.hasLunchIn,
                    tooltip: s.hasCompletedPrimary
                      ? 'Almoço bloqueado após a primeira saída'
                      : s.hasLunchIn
                        ? 'Volta do almoço já registrada'
                        : !s.hasLunchOut
                          ? 'Registre a saída para o almoço antes'
                          : 'Registrar volta do almoço',
                  },
                  {
                    kind: 'check_out',
                    title: s.hasCompletedPrimary ? 'Encerrar' : 'Saída',
                    time: s.checkOutAt ? formatTime(s.checkOutAt) : null,
                    done: s.hasCompletedPrimary && !s.sessionOpen,
                    enabled:
                      !loading && s.sessionOpen && !s.awaitingLunchReturn,
                    tooltip: !s.sessionOpen
                      ? s.canReopen
                        ? 'Expediente encerrado — reabra para hora extra'
                        : 'Registre a entrada antes da saída'
                      : s.awaitingLunchReturn
                        ? 'Registre a volta do almoço antes da saída do expediente'
                        : 'Registrar saída do expediente',
                  },
                ];
                const nextKind = steps.find((st) => st.enabled && !st.done)?.kind ?? null;
                return (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0, 1fr))' },
                      gap: 1.25,
                    }}
                  >
                    {steps.map((st, i) => (
                      <PunchStepCard
                        key={st.kind}
                        kind={st.kind}
                        title={st.title}
                        step={i + 1}
                        time={st.time}
                        done={st.done}
                        next={nextKind === st.kind}
                        disabled={!st.enabled}
                        tooltip={st.tooltip}
                        onClick={() => void punch(st.kind)}
                      />
                    ))}
                  </Box>
                );
              })()}
            </Box>
          </Stack>

          {summary ? (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Card sx={{ p: 2, border: 1, borderColor: 'divider', height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">
                    Saldo banco de horas
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: balanceColor(summary.balanceMinutes) }}>
                    {formatMinutes(summary.balanceMinutes)}
                  </Typography>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Card sx={{ p: 2, border: 1, borderColor: 'divider', height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">
                    Bônus no mês
                  </Typography>
                  <Box sx={{ my: 0.5 }}>
                    <StatusChip
                      label={summary.bonus.eligible ? 'Elegível' : 'Não elegível'}
                      tone={summary.bonus.eligible ? 'success' : 'warning'}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {formatBonusUnjustifiedCounter(summary.bonus)}
                  </Typography>
                  {!summary.bonus.eligible && summary.bonus.reason ? (
                    <Typography variant="body2" color="text.secondary">
                      {summary.bonus.reason}
                    </Typography>
                  ) : null}
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Card sx={{ p: 2, border: 1, borderColor: 'divider', height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">
                    Justificativas pendentes
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    {summary.justificationsPending}
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          ) : null}

          <Typography variant="h6" component="h2" sx={sectionTitleSx}>
            Resumo por dia ({month.toString().padStart(2, '0')}/{year})
          </Typography>
          <TableContainer component={Paper} sx={{ overflowX: 'auto', border: 1, borderColor: 'divider' }}>
            <Table
              size="small"
              sx={{
                minWidth: 720,
                '& thead th': {
                  fontSize: '0.68rem',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'text.secondary',
                  bgcolor: 'action.hover',
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  whiteSpace: 'nowrap',
                },
                '& tbody td': { borderColor: 'divider' },
                '& .tc-time': { fontVariantNumeric: 'tabular-nums', textAlign: 'center', whiteSpace: 'nowrap' },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell align="center">Entrada</TableCell>
                  <TableCell align="center">Saída almoço</TableCell>
                  <TableCell align="center">Volta almoço</TableCell>
                  <TableCell align="center">Saída</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Saldo dia</TableCell>
                  <TableCell align="center">Justif.</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {days.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
                      Nenhum dia registrado neste mês.
                    </TableCell>
                  </TableRow>
                ) : (
                  days.map((d) => {
                    const hasJust = justifications.some((j) => j.workDate === d.workDate);
                    const isToday = d.workDate === formatWorkDateLocal();
                    const saldo =
                      isToday && todayLiveBalanceMinutes != null
                        ? todayLiveBalanceMinutes
                        : d.dailyBalanceMinutes;
                    const muted = (v: string) =>
                      v === '—' ? <Box component="span" sx={{ color: 'text.disabled' }}>—</Box> : v;
                    return (
                      <TableRow
                        key={d.id}
                        hover
                        sx={{
                          '&:last-of-type td': { borderBottom: 0 },
                          ...(isToday
                            ? {
                                bgcolor: 'rgba(43,108,176,0.06)',
                                '& td:first-of-type': {
                                  borderLeft: '3px solid',
                                  borderColor: 'primary.main',
                                },
                              }
                            : {}),
                        }}
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: isToday ? 800 : 600 }}>
                          {formatDateBr(d.workDate)}
                          {isToday ? (
                            <Box component="span" sx={{ ml: 0.75, fontSize: '0.68rem', fontWeight: 800, color: 'primary.main' }}>
                              HOJE
                            </Box>
                          ) : null}
                        </TableCell>
                        <TableCell className="tc-time">{muted(formatTime(d.firstCheckInAt))}</TableCell>
                        <TableCell className="tc-time">{muted(formatTime(d.lunchOutAt))}</TableCell>
                        <TableCell className="tc-time">{muted(formatTime(d.lunchInAt))}</TableCell>
                        <TableCell className="tc-time">{muted(formatTime(d.lastCheckOutAt))}</TableCell>
                        <TableCell>
                          <StatusChip label={d.statusLabel || '—'} tone={balanceTone(saldo)} />
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                          <BalanceText minutes={saldo} />
                        </TableCell>
                        <TableCell align="center">
                          {hasJust ? (
                            <StatusChip label="Sim" tone="accent" />
                          ) : (
                            <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TcPagination
            page={daysPagination.page}
            totalPages={daysPagination.totalPages}
            total={daysPagination.total}
            pageSize={daysPagination.pageSize}
            onPageChange={setDaysPage}
            disabled={loading}
            itemLabel="dias"
            onPageSizeChange={(size) => {
              setDaysPageSize(size);
              setDaysPage(1);
            }}
          />

          <Box sx={{ mt: 2 }}>
            <TcAccordionSection
              panelId="tc-recent-entries-panel"
              title="Últimos registros"
              subtitle={
                recentEntriesExpanded
                  ? 'Recolher lista de batidas'
                  : entriesPagination.total === 0
                    ? 'Nenhuma batida neste mês — expandir para detalhes'
                    : `Expandir para ver ${entriesPagination.total} batida${entriesPagination.total === 1 ? '' : 's'} do mês`
              }
              expanded={recentEntriesExpanded}
              onToggle={() => setRecentEntriesExpanded((prev) => !prev)}
            >
              {entriesPagination.total === 0 ? (
                <Typography color="text.secondary">Nenhuma batida registrada neste mês.</Typography>
              ) : (
                <>
                  <Stack spacing={1} sx={{ mb: 1 }}>
                    {entries.map((e) => (
                      <Stack
                        key={e.id}
                        direction="row"
                        spacing={1.5}
                        sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                      >
                        <StatusChip label={formatTimeclockEntryKind(e.kind)} tone="accent" />
                        <Typography component="time" variant="body2" dateTime={e.occurredAt}>
                          {formatDateBr(e.occurredAt)} {formatTime(e.occurredAt)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                  <TcPagination
                    page={entriesPagination.page}
                    totalPages={entriesPagination.totalPages}
                    total={entriesPagination.total}
                    pageSize={entriesPagination.pageSize}
                    onPageChange={setEntriesPage}
                    disabled={loading}
                    itemLabel="batidas"
                    onPageSizeChange={(size) => {
                      setEntriesPageSize(size);
                      setEntriesPage(1);
                    }}
                  />
                </>
              )}
            </TcAccordionSection>
          </Box>
        </Paper>
      ) : null}

      {tab === 'just' ? (
        <Paper sx={panelSx}>
          <Typography variant="h6" component="h2" sx={sectionTitleSx}>
            Nova justificativa
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Data"
                type="date"
                fullWidth
                value={justForm.workDate}
                onChange={(e) => setJustForm((p) => ({ ...p, workDate: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PremiumSelect
                id="tc-just-type"
                label="Tipo"
                value={justForm.type}
                options={justificationTypeOptions}
                onChange={(v) => setJustForm((p) => ({ ...p, type: v as TimeJustificationType }))}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Descrição"
                fullWidth
                multiline
                rows={4}
                value={justForm.description}
                onChange={(e) => setJustForm((p) => ({ ...p, description: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                Anexo (atestado, declaração, etc.)
              </Typography>
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Button variant="outlined" component="label">
                  Escolher arquivo
                  <input
                    ref={justFileInputRef}
                    type="file"
                    hidden
                    aria-label="Escolher arquivo anexo à justificativa"
                    onChange={(e) => setJustFile(e.target.files?.[0] ?? null)}
                  />
                </Button>
                <Typography variant="body2" color="text.secondary" aria-live="polite">
                  {justFile ? justFile.name : 'Nenhum arquivo selecionado'}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" startIcon={<SendOutlinedIcon />} onClick={() => void submitJustification()}>
              Enviar
            </Button>
          </Box>

          <Typography variant="h6" component="h2" sx={sectionTitleSx}>
            Histórico
          </Typography>
          <Stack spacing={1.5}>
            {justifications.map((j) => (
              <Paper key={j.id} sx={{ p: 2, border: 1, borderColor: 'divider' }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography component="strong" sx={{ fontWeight: 700 }}>
                    {formatDateBr(j.workDate)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    · {JUST_TYPES.find((x) => x.value === j.type)?.label}
                  </Typography>
                  <StatusChip label={statusJustPt[j.status] || j.status} tone={approvalTone(j.status)} />
                </Stack>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {j.description}
                </Typography>
                {j.attachments?.length ? (
                  <Typography variant="caption" color="text.secondary">
                    {j.attachments.length} anexo(s)
                  </Typography>
                ) : null}
              </Paper>
            ))}
          </Stack>
        </Paper>
      ) : null}

      {tab === 'banco' ? (
        <Paper sx={panelSx}>
          {summary ? (
            <Card sx={{ p: 2, border: 1, borderColor: 'divider', mb: 2, maxWidth: 320 }}>
              <Typography variant="body2" color="text.secondary">
                Saldo do banco de horas
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: balanceColor(summary.balanceMinutes) }}>
                {formatMinutes(summary.balanceMinutes)}
              </Typography>
            </Card>
          ) : null}
          <Typography variant="h6" component="h2" sx={sectionTitleSx}>
            Nova solicitação
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label="Quantidade"
                type="number"
                fullWidth
                value={bankForm.amount}
                onChange={(e) =>
                  setBankForm((p) => ({ ...p, amount: normalizeBankAmountInput(e.target.value) }))
                }
                slotProps={{
                  htmlInput: {
                    min: bankForm.unit === 'minutes' ? 1 : 0.5,
                    step: bankForm.unit === 'minutes' ? 1 : 0.5,
                    'aria-label': 'Valor da quantidade',
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <PremiumSelect
                id="tc-bank-amount-unit"
                label="Unidade"
                value={bankForm.unit}
                options={BANK_AMOUNT_UNIT_OPTIONS}
                onChange={(v) => setBankForm((p) => ({ ...p, unit: v as BankAmountUnit }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <PremiumSelect
                id="tc-bank-disposition"
                label="Destino"
                value={bankForm.disposition}
                options={BANK_DISPOSITION_OPTIONS}
                onChange={(v) =>
                  setBankForm((p) => ({ ...p, disposition: v as 'compensation' | 'payment' }))
                }
              />
            </Grid>
            {bankForm.disposition === 'compensation' ? (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <PremiumSelect
                  id="tc-bank-compensation-mode"
                  label="Como compensar"
                  value={bankForm.compensationMode}
                  options={BANK_COMPENSATION_MODE_OPTIONS}
                  onChange={(v) =>
                    setBankForm((p) => ({ ...p, compensationMode: v as 'single_day' | 'range' }))
                  }
                />
              </Grid>
            ) : null}
            {bankForm.disposition === 'compensation' &&
            bankForm.compensationMode === 'single_day' ? (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  label="Dia de compensação"
                  type="date"
                  fullWidth
                  required
                  value={bankForm.compensationDate}
                  onChange={(e) =>
                    setBankForm((p) => ({ ...p, compensationDate: e.target.value }))
                  }
                />
              </Grid>
            ) : null}
            {bankForm.disposition === 'compensation' &&
            bankForm.compensationMode === 'range' ? (
              <>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    label="De"
                    type="date"
                    fullWidth
                    required
                    value={bankForm.compensationStartDate}
                    onChange={(e) =>
                      setBankForm((p) => ({ ...p, compensationStartDate: e.target.value }))
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    label="Até"
                    type="date"
                    fullWidth
                    required
                    value={bankForm.compensationEndDate}
                    onChange={(e) =>
                      setBankForm((p) => ({ ...p, compensationEndDate: e.target.value }))
                    }
                    slotProps={{
                      htmlInput: { min: bankForm.compensationStartDate || undefined },
                    }}
                  />
                </Grid>
              </>
            ) : null}
            <Grid size={{ xs: 12 }}>
              <Typography variant="caption" color="text.secondary">
                {bankPreviewMinutes >= 1
                  ? `Equivale a ${formatMinutes(bankPreviewMinutes)}${
                      bankForm.unit === 'days' ? ' (1 dia = 8h)' : ''
                    }`
                  : bankForm.unit === 'days'
                    ? '1 dia = 8 horas (jornada líquida).'
                    : 'Informe um valor maior que zero.'}
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" startIcon={<SendOutlinedIcon />} onClick={() => void submitBank()}>
              Solicitar
            </Button>
          </Box>

          <Box sx={{ mt: 2 }}>
            <TcAccordionSection
              panelId="tc-my-bank-panel"
              title="Minhas solicitações"
              subtitle={tcAccordionListSubtitle(
                myBankExpanded,
                bankReqs.length,
                'solicitação',
                'Nenhuma solicitação de banco de horas — expandir para detalhes',
              )}
              expanded={myBankExpanded}
              onToggle={() => setMyBankExpanded((prev) => !prev)}
            >
              {bankReqs.length === 0 ? (
                <Typography color="text.secondary">Nenhuma solicitação enviada.</Typography>
              ) : (
                <Stack spacing={1}>
                  {bankReqs.map((r) => (
                    <Stack
                      key={r.id}
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                    >
                      <Typography component="strong" sx={{ fontWeight: 700 }}>
                        {formatMinutes(r.minutes)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        · {formatBankCompensationLabel(r)} ·
                      </Typography>
                      <StatusChip label={statusBankPt[r.status]} tone={approvalTone(r.status)} />
                    </Stack>
                  ))}
                </Stack>
              )}
            </TcAccordionSection>
          </Box>
        </Paper>
      ) : null}

      {tab === 'correcao' ? (
        <Paper sx={panelSx}>
          <Typography sx={hintSx}>
            Use <strong>Corrigir batida já existente</strong> para ajustar um horário errado ou incluir
            almoço esquecido na jornada (saída e, depois, volta — mesmo se a saída do dia já existir). Se o
            expediente já foi encerrado e você precisa registrar serviço extra no mesmo dia, escolha{' '}
            <strong>Adicionar batida extra</strong> (entrada e, em seguida, saída). Após aprovação, o ponto
            é aplicado automaticamente.
          </Typography>
          <Typography variant="h6" component="h2" sx={sectionTitleSx}>
            Nova solicitação ao RH
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Data do ponto"
                type="date"
                fullWidth
                value={correctionForm.workDate}
                onChange={(e) => setCorrectionForm((p) => ({ ...p, workDate: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PremiumSelect
                id="tc-correction-apply-mode"
                label="O que deseja fazer"
                value={correctionForm.applyMode}
                options={CORRECTION_APPLY_MODE_OPTIONS}
                onChange={(v) => {
                  const applyMode = v as TimeCorrectionApplyMode;
                  setCorrectionForm((p) => ({
                    ...p,
                    applyMode,
                    kind:
                      applyMode === 'add_extra' && (p.kind === 'lunch_out' || p.kind === 'lunch_in')
                        ? 'check_in'
                        : p.kind,
                  }));
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PremiumSelect
                id="tc-correction-kind"
                label="Tipo de batida"
                value={correctionForm.kind}
                options={
                  correctionForm.applyMode === 'add_extra'
                    ? CORRECTION_EXTRA_KIND_OPTIONS
                    : CORRECTION_KIND_OPTIONS
                }
                onChange={(v) => setCorrectionForm((p) => ({ ...p, kind: v as TimeClockEntryKind }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Horário correto (Brasília)"
                type="time"
                fullWidth
                value={correctionForm.requestedTime}
                onChange={(e) => setCorrectionForm((p) => ({ ...p, requestedTime: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Motivo / observação"
                fullWidth
                multiline
                rows={4}
                value={correctionForm.description}
                onChange={(e) => setCorrectionForm((p) => ({ ...p, description: e.target.value }))}
                placeholder={
                  correctionForm.applyMode === 'add_extra'
                    ? 'Ex.: serviço fora do expediente; enviar entrada extra e depois a saída extra.'
                    : 'Ex.: esqueci de bater a saída; registro com horário errado no sistema.'
                }
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" startIcon={<SendOutlinedIcon />} onClick={() => void submitTimeCorrection()}>
              Enviar ao RH
            </Button>
          </Box>

          <Box sx={{ mt: 2 }}>
            <TcAccordionSection
              panelId="tc-my-corrections-panel"
              title="Minhas solicitações"
              subtitle={tcAccordionListSubtitle(
                myCorrectionsExpanded,
                timeCorrections.length,
                'solicitação',
                'Nenhuma solicitação de correção — expandir para detalhes',
              )}
              expanded={myCorrectionsExpanded}
              onToggle={() => setMyCorrectionsExpanded((prev) => !prev)}
            >
              {timeCorrections.length === 0 ? (
                <Typography color="text.secondary">Nenhuma solicitação de correção enviada.</Typography>
              ) : (
                <Stack spacing={1.5}>
                  {timeCorrections.map((r) => (
                    <Box key={r.id}>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography component="strong" sx={{ fontWeight: 700 }}>
                          {formatDateBr(r.workDate)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          · {formatTimeclockApplyMode(r.applyMode)} · {formatTimeclockEntryKind(r.kind)} ·{' '}
                          {r.requestedTime}
                        </Typography>
                        <StatusChip
                          label={statusCorrectionPt[r.status] || r.status}
                          tone={approvalTone(r.status)}
                        />
                      </Stack>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {r.description}
                      </Typography>
                      {r.rejectReason ? (
                        <Typography variant="caption" color="text.secondary">
                          Motivo: {r.rejectReason}
                        </Typography>
                      ) : null}
                    </Box>
                  ))}
                </Stack>
              )}
            </TcAccordionSection>
          </Box>
        </Paper>
      ) : null}

      {tab === 'nf' ? (
        <Paper sx={panelSx}>
          <Typography sx={hintSx}>
            Envie sua nota fiscal para análise do RH. Anexe o documento (PDF, imagem ou XML) e descreva a
            observação relevante para o pagamento.
          </Typography>
          {invoiceForm.purpose === 'salary' ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Esta nota é do <strong>pagamento do mês (salário)</strong>. Depois que o RH aprovar, o valor
              entra automaticamente na folha de pagamentos — sem lançar como reembolso.
            </Alert>
          ) : null}
          <Typography variant="h6" component="h2" sx={sectionTitleSx}>
            Nova nota fiscal
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <PremiumSelect
                id="tc-invoice-purpose"
                label="Tipo da nota"
                value={invoiceForm.purpose}
                options={[
                  { value: 'salary', label: 'Pagamento do mês (salário)' },
                  { value: 'other', label: 'Outra nota (avulsa)' },
                ]}
                onChange={(v) =>
                  setInvoiceForm((p) => ({ ...p, purpose: v as 'salary' | 'other' }))
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <PremiumSelect
                id="tc-invoice-month"
                label="Competência (mês)"
                value={String(month)}
                options={MONTH_SELECT_OPTIONS}
                onChange={(v) => setMonth(Number(v))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label="Ano (competência)"
                type="number"
                fullWidth
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                slotProps={{ htmlInput: { min: 2020, max: 2040 } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label="Data de emissão"
                type="date"
                fullWidth
                value={invoiceForm.issueDate}
                onChange={(e) => setInvoiceForm((p) => ({ ...p, issueDate: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label={invoiceForm.purpose === 'salary' ? 'Valor (R$) — obrigatório' : 'Valor (R$) — opcional'}
                fullWidth
                required={invoiceForm.purpose === 'salary'}
                value={invoiceForm.amount}
                onChange={(e) => setInvoiceForm((p) => ({ ...p, amount: e.target.value }))}
                placeholder="Ex.: 1500,00"
                helperText={
                  invoiceForm.purpose === 'salary'
                    ? 'Esse valor será lançado como salário na folha após a aprovação.'
                    : undefined
                }
                slotProps={{ htmlInput: { inputMode: 'decimal' } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <TextField
                label="Número da nota — opcional"
                fullWidth
                value={invoiceForm.invoiceNumber}
                onChange={(e) => setInvoiceForm((p) => ({ ...p, invoiceNumber: e.target.value }))}
                placeholder="Ex.: 12345"
                slotProps={{ htmlInput: { maxLength: 64 } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Observação"
                fullWidth
                multiline
                rows={4}
                value={invoiceForm.notes}
                onChange={(e) => setInvoiceForm((p) => ({ ...p, notes: e.target.value }))}
                placeholder="Ex.: referente aos serviços prestados em maio; dados bancários já cadastrados."
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                Arquivos da nota fiscal (PDF, JPG, PNG ou XML — até 10)
              </Typography>
              <Button variant="outlined" component="label">
                Escolher arquivos
                <input
                  ref={invoiceFileInputRef}
                  type="file"
                  hidden
                  multiple
                  accept=".pdf,.png,.jpg,.jpeg,.xml,application/pdf,image/*,application/xml,text/xml"
                  aria-label="Escolher arquivos da nota fiscal"
                  onChange={(e) => {
                    const selected = Array.from(e.target.files ?? []);
                    if (!selected.length) return;
                    setInvoiceFiles((prev) => {
                      const merged = [...prev];
                      for (const file of selected) {
                        const dup = merged.some(
                          (f) =>
                            f.name === file.name &&
                            f.size === file.size &&
                            f.lastModified === file.lastModified,
                        );
                        if (!dup) merged.push(file);
                      }
                      return merged.slice(0, 10);
                    });
                    e.target.value = '';
                  }}
                />
              </Button>
              {invoiceFiles.length === 0 ? (
                <Typography variant="body2" color="text.secondary" aria-live="polite" sx={{ mt: 1 }}>
                  Nenhum arquivo selecionado
                </Typography>
              ) : (
                <Stack spacing={1} sx={{ mt: 1 }} aria-live="polite">
                  {invoiceFiles.map((file, idx) => (
                    <Stack
                      key={`${file.name}-${file.size}-${file.lastModified}-${idx}`}
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                    >
                      <Typography variant="body2">{file.name}</Typography>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => setInvoiceFiles((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        Remover
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Grid>
          </Grid>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              disabled={invoiceSubmitInProgress}
              startIcon={<SendOutlinedIcon />}
              onClick={() => void submitInvoice()}
            >
              {invoiceSubmitInProgress ? 'Enviando…' : 'Enviar ao RH'}
            </Button>
          </Box>

          {user?.id ? (
            <Box sx={{ mt: 2 }}>
              <MyInvoiceObligationSummary viewerId={user.id} />
            </Box>
          ) : null}

          <Box sx={{ mt: 2 }}>
            <TcAccordionSection
              panelId="tc-my-invoices-panel"
              title="Minhas notas fiscais"
              subtitle={
                myInvoicesExpanded
                  ? 'Recolher histórico de notas fiscais'
                  : 'Expandir histórico de notas fiscais'
              }
              expanded={myInvoicesExpanded}
              onToggle={() => setMyInvoicesExpanded((prev) => !prev)}
            >
              {myInvoicesExpanded ? (
                <InvoiceHistoryPanel mode="mine" refreshKey={invoiceHistoryVersion} />
              ) : null}
            </TcAccordionSection>
          </Box>
        </Paper>
      ) : null}

      {tab === 'gestao' && isManagerial ? (
        <Paper sx={panelSx}>
          {/* Cabeçalho da seção */}
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mb: 2.5 }}>
            <Box
              sx={{
                flexShrink: 0,
                width: 48,
                height: 48,
                display: { xs: 'none', sm: 'inline-flex' },
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '& svg': { fontSize: 26 },
              }}
            >
              <SupervisorAccountOutlinedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography component="span" sx={tcKickerSx}>
                Gestão de ponto
              </Typography>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                Acompanhamento da equipe
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                Escolha o período e o colaborador para revisar batidas, aprovar solicitações e conferir a
                elegibilidade de bônus do mês.
              </Typography>
            </Box>
          </Stack>

          {/* Barra de filtros: período + colaborador */}
          <Box
            sx={{
              p: { xs: 1.5, md: 2 },
              bgcolor: 'action.hover',
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '140px 110px', md: '160px 120px 1fr' },
              gap: 1.5,
              alignItems: 'end',
            }}
          >
            <PremiumSelect
              id="tc-gestao-month"
              label="Mês (equipe)"
              value={String(month)}
              options={MONTH_SELECT_OPTIONS}
              onChange={(v) => setMonth(Number(v))}
            />
            <TextField
              label="Ano"
              type="number"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              slotProps={{ htmlInput: { min: 2020, max: 2040 } }}
              fullWidth
            />
            <Box sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}>
              <PremiumSelect
                id="tc-team-member"
                label="Colaborador"
                value={teamUserId}
                options={teamMemberSelectOptions}
                onChange={setTeamUserId}
                placeholder="Selecione um colaborador..."
              />
            </Box>
          </Box>

          {canEditTeamPunches && teamUserId ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" component="h3" sx={subsectionTitleSx}>
                Registrar ponto do colaborador
              </Typography>
              <Typography sx={hintSx}>
                Os botões rápidos consideram o dia de hoje: com entrada registrada, só a saída fica
                disponível. No formulário você pode informar outra data e hora (horário de Brasília).
                Atualização automática a cada 5 s.
              </Typography>
              <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  disabled={
                    teamPunchForMemberInProgress ||
                    !!creatingTeamEntryKind ||
                    selectedMemberTodayPunch.hasCheckIn
                  }
                  onClick={() => openCreateTeamEntry('check_in')}
                >
                  Registrar entrada
                </Button>
                <Button
                  variant="outlined"
                  disabled={
                    teamPunchForMemberInProgress ||
                    !!creatingTeamEntryKind ||
                    !selectedMemberTodayPunch.hasCheckIn ||
                    selectedMemberTodayPunch.hasCheckOut
                  }
                  onClick={() => openCreateTeamEntry('check_out')}
                >
                  Registrar saída
                </Button>
              </Stack>
            </Box>
          ) : null}

          <Typography id="tc-gestao-resumo" variant="subtitle1" component="h3" sx={subsectionTitleSx}>
            Resumo por dia
          </Typography>
          <Typography sx={hintSx}>
            {canEditTeamPunches ? (
              <>
                Valores agregados por dia útil. Para corrigir batidas, use <strong>Corrigir batidas</strong>{' '}
                na linha do dia: você pode <strong>Editar</strong> cada registro e, na edição,{' '}
                <strong>excluir</strong> a batida se necessário. O resumo e o banco de horas são recalculados
                ao salvar ou excluir.
              </>
            ) : (
              'Valores agregados por dia útil.'
            )}
          </Typography>
          {teamGestaoLoading && teamDays.length === 0 ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <CircularProgress size={18} />
              <Typography color="text.secondary">Carregando…</Typography>
            </Stack>
          ) : !teamGestaoLoading && teamDaysPagination.total === 0 ? (
            <Typography color="text.secondary">
              Nenhum dia com registro neste mês para este colaborador.
            </Typography>
          ) : null}
          {teamDays.length > 0 || (!teamGestaoLoading && teamDaysPagination.total > 0) ? (
            <TableContainer
              component={Paper}
              sx={{ overflowX: 'auto', border: 1, borderColor: 'divider', opacity: teamGestaoLoading ? 0.6 : 1 }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Entrada</TableCell>
                    <TableCell>Saída almoço</TableCell>
                    <TableCell>Volta almoço</TableCell>
                    <TableCell>Saída</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Saldo dia</TableCell>
                    {canEditTeamPunches ? <TableCell align="right">Ações</TableCell> : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {teamDays.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell>{formatDateBr(d.workDate)}</TableCell>
                      <TableCell>{formatTime(d.firstCheckInAt)}</TableCell>
                      <TableCell>{formatTime(d.lunchOutAt)}</TableCell>
                      <TableCell>{formatTime(d.lunchInAt)}</TableCell>
                      <TableCell>{formatTime(d.lastCheckOutAt)}</TableCell>
                      <TableCell>
                        <StatusChip
                          label={d.statusLabel || '—'}
                          tone={balanceTone(d.dailyBalanceMinutes)}
                        />
                      </TableCell>
                      <TableCell>
                        <BalanceText minutes={d.dailyBalanceMinutes} />
                      </TableCell>
                      {canEditTeamPunches ? (
                        <TableCell align="right">
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => setDayPunchesModalWorkDate(d.workDate)}
                          >
                            Corrigir batidas
                          </Button>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
          <TcPagination
            page={teamDaysPagination.page}
            totalPages={teamDaysPagination.totalPages}
            total={teamDaysPagination.total}
            pageSize={teamDaysPagination.pageSize}
            onPageChange={setTeamDaysPage}
            disabled={teamGestaoLoading}
            itemLabel="dias"
          />

          <Typography variant="subtitle1" component="h3" sx={subsectionTitleSx}>
            Log de batidas do mês
          </Typography>
          <Typography sx={hintSx}>
            Histórico de entradas e saídas do colaborador no mês selecionado (inclui registros feitos pelo
            próprio colaborador).
          </Typography>
          {teamGestaoLoading && teamEntries.length === 0 ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <CircularProgress size={18} />
              <Typography color="text.secondary">Carregando…</Typography>
            </Stack>
          ) : !teamGestaoLoading && teamEntriesPagination.total === 0 ? (
            <Typography color="text.secondary">Nenhuma batida neste mês para este colaborador.</Typography>
          ) : teamEntries.length > 0 ? (
            <TableContainer
              component={Paper}
              sx={{ overflowX: 'auto', border: 1, borderColor: 'divider', opacity: teamGestaoLoading ? 0.6 : 1 }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Hora</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Registrado em</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {teamEntries.map((en) => {
                    const br = isoToBrazilDateAndTime(en.occurredAt);
                    return (
                      <TableRow key={en.id}>
                        <TableCell>{formatYmdDashToBr(br.workDate)}</TableCell>
                        <TableCell>{br.timeHM}</TableCell>
                        <TableCell>{formatTimeclockEntryKind(en.kind)}</TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {new Date(en.createdAt).toLocaleString('pt-BR', {
                            timeZone: 'America/Sao_Paulo',
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
          <TcPagination
            page={teamEntriesPagination.page}
            totalPages={teamEntriesPagination.totalPages}
            total={teamEntriesPagination.total}
            pageSize={teamEntriesPagination.pageSize}
            onPageChange={setTeamEntriesPage}
            disabled={teamGestaoLoading}
            itemLabel="batidas"
          />

          {canReviewTimeCorrections ? (
            <Box sx={{ mt: 2 }}>
              <TcAccordionSection
                id="tc-hr-corrections"
                panelId="tc-hr-corrections-panel"
                className="tc-hr-corrections-section"
                title="Solicitações de correção de hora"
                subtitle={tcAccordionListSubtitle(
                  gestaoCorrectionsExpanded,
                  hrTimeCorrections.length,
                  'solicitação',
                  'Nenhuma solicitação neste escopo — expandir para detalhes',
                )}
                expanded={gestaoCorrectionsExpanded}
                onToggle={() => setGestaoCorrectionsExpanded((prev) => !prev)}
              >
                <Typography sx={hintSx}>
                  {canEditTeamPunches
                    ? 'Pedidos para corrigir um horário ou adicionar hora extra (reabertura). Ao aprovar hora extra, aplique a entrada extra antes da saída extra. O RH pode editar ou excluir solicitações mesmo após aprovação ou rejeição; alterações em solicitações aprovadas atualizam o ponto do colaborador.'
                    : 'Pedidos seus e dos colaboradores do seu time para corrigir horário ou adicionar hora extra. Ao aprovar hora extra, aplique a entrada extra antes da saída extra. Você pode editar ou excluir solicitações do seu escopo mesmo após aprovação ou rejeição.'}
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <ApprovalStatusFilter
                    ariaLabel="Filtrar correções de hora por situação"
                    value={correctionStatusFilter}
                    options={buildApprovalFilterOptions(correctionStatusCounts)}
                    onChange={setCorrectionStatusFilter}
                  />
                </Box>
                {filteredHrTimeCorrections.length === 0 ? (
                  <Typography color="text.secondary">
                    {hrTimeCorrections.length === 0
                      ? 'Nenhuma solicitação neste escopo.'
                      : 'Nenhuma solicitação neste filtro.'}
                  </Typography>
                ) : (
                  <Stack spacing={2}>
                    {filteredHrTimeCorrections.map((r) => (
                      <Paper key={r.id} sx={{ p: 2, border: 1, borderColor: 'divider' }}>
                        {editingHrCorrectionId === r.id && hrCorrectionEditForm ? (
                          <Box>
                            <Typography component="strong" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>
                              {r.userName || memberNameMap[r.userId] || r.userId}
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                  label="Data do ponto"
                                  type="date"
                                  fullWidth
                                  value={hrCorrectionEditForm.workDate}
                                  onChange={(e) =>
                                    setHrCorrectionEditForm((p) =>
                                      p ? { ...p, workDate: e.target.value } : p,
                                    )
                                  }
                                />
                              </Grid>
                              <Grid size={{ xs: 12, sm: 6 }}>
                                <PremiumSelect
                                  id={`tc-hr-correction-apply-mode-${r.id}`}
                                  label="Tipo de solicitação"
                                  value={hrCorrectionEditForm.applyMode}
                                  options={CORRECTION_APPLY_MODE_OPTIONS}
                                  onChange={(v) => {
                                    const applyMode = v as TimeCorrectionApplyMode;
                                    setHrCorrectionEditForm((p) =>
                                      p
                                        ? {
                                            ...p,
                                            applyMode,
                                            kind:
                                              applyMode === 'add_extra' &&
                                              (p.kind === 'lunch_out' || p.kind === 'lunch_in')
                                                ? 'check_in'
                                                : p.kind,
                                          }
                                        : p,
                                    );
                                  }}
                                />
                              </Grid>
                              <Grid size={{ xs: 12, sm: 6 }}>
                                <PremiumSelect
                                  id={`tc-hr-correction-kind-${r.id}`}
                                  label="Tipo de batida"
                                  value={hrCorrectionEditForm.kind}
                                  options={
                                    hrCorrectionEditForm.applyMode === 'add_extra'
                                      ? CORRECTION_EXTRA_KIND_OPTIONS
                                      : CORRECTION_KIND_OPTIONS
                                  }
                                  onChange={(v) =>
                                    setHrCorrectionEditForm((p) =>
                                      p ? { ...p, kind: v as TimeClockEntryKind } : p,
                                    )
                                  }
                                />
                              </Grid>
                              <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                  label="Horário (Brasília)"
                                  type="time"
                                  fullWidth
                                  value={hrCorrectionEditForm.requestedTime}
                                  onChange={(e) =>
                                    setHrCorrectionEditForm((p) =>
                                      p ? { ...p, requestedTime: e.target.value } : p,
                                    )
                                  }
                                />
                              </Grid>
                              <Grid size={{ xs: 12, sm: 6 }}>
                                <PremiumSelect
                                  id={`tc-hr-correction-status-${r.id}`}
                                  label="Situação"
                                  value={hrCorrectionEditForm.decision}
                                  options={HR_CORRECTION_DECISION_OPTIONS}
                                  onChange={(v) =>
                                    setHrCorrectionEditForm((p) =>
                                      p ? { ...p, decision: v as TimeCorrectionRequestStatus } : p,
                                    )
                                  }
                                />
                              </Grid>
                              <Grid size={{ xs: 12 }}>
                                <TextField
                                  label="Motivo / observação"
                                  fullWidth
                                  multiline
                                  rows={3}
                                  value={hrCorrectionEditForm.description}
                                  onChange={(e) =>
                                    setHrCorrectionEditForm((p) =>
                                      p ? { ...p, description: e.target.value } : p,
                                    )
                                  }
                                />
                              </Grid>
                              {hrCorrectionEditForm.decision === 'rejected' ? (
                                <Grid size={{ xs: 12 }}>
                                  <TextField
                                    label="Motivo da rejeição"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    value={hrCorrectionEditForm.rejectReason}
                                    onChange={(e) =>
                                      setHrCorrectionEditForm((p) =>
                                        p ? { ...p, rejectReason: e.target.value } : p,
                                      )
                                    }
                                  />
                                </Grid>
                              ) : null}
                            </Grid>
                            <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => void saveHrCorrectionEdit()}
                              >
                                Salvar alterações
                              </Button>
                              <Button variant="outlined" size="small" onClick={cancelHrCorrectionEdit}>
                                Cancelar
                              </Button>
                            </Stack>
                          </Box>
                        ) : (
                          <>
                            <Typography variant="body2">
                              <strong>{r.userName || memberNameMap[r.userId] || r.userId}</strong> ·{' '}
                              {formatDateBr(r.workDate)} · {formatTimeclockApplyMode(r.applyMode)} ·{' '}
                              {formatTimeclockEntryKind(r.kind)} · {r.requestedTime} ·{' '}
                              {statusCorrectionPt[r.status] || r.status}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {r.description}
                            </Typography>
                            {r.rejectReason ? (
                              <Typography variant="caption" color="text.secondary">
                                Motivo da rejeição: {r.rejectReason}
                              </Typography>
                            ) : null}
                            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                              {r.status === 'pending' ? (
                                <>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => void reviewTimeCorrection(r.id, 'approved')}
                                  >
                                    Aprovar e aplicar
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => void reviewTimeCorrection(r.id, 'rejected')}
                                  >
                                    Rejeitar
                                  </Button>
                                </>
                              ) : null}
                              <Button
                                variant="text"
                                size="small"
                                startIcon={<EditOutlinedIcon />}
                                onClick={() => openHrCorrectionEdit(r)}
                              >
                                Editar
                              </Button>
                              <Button
                                variant="text"
                                size="small"
                                color="error"
                                startIcon={<DeleteOutlinedIcon />}
                                onClick={() => requestDeleteHrCorrection(r.id)}
                              >
                                Excluir
                              </Button>
                            </Stack>
                          </>
                        )}
                      </Paper>
                    ))}
                  </Stack>
                )}
              </TcAccordionSection>
            </Box>
          ) : null}

          {canReviewInvoices ? (
            <Box sx={{ mt: 2 }}>
              <TcAccordionSection
                id="tc-hr-invoices"
                panelId="tc-hr-invoices-panel"
                className="tc-hr-invoices-section"
                title="Notas fiscais (RH)"
                subtitle={
                  gestaoInvoicesExpanded
                    ? 'Recolher histórico de notas fiscais'
                    : 'Expandir histórico de notas fiscais'
                }
                expanded={gestaoInvoicesExpanded}
                onToggle={() => setGestaoInvoicesExpanded((prev) => !prev)}
              >
                {gestaoInvoicesExpanded ? (
                  <InvoiceManagementTabs
                    receivedNotes={
                      <InvoiceHistoryPanel
                        mode="review"
                        refreshKey={invoiceHistoryVersion}
                        collaborators={
                          user?.role === 'manager'
                            ? teamMembers.filter((member) => member.id !== user.id)
                            : teamMembers
                        }
                        canReview
                        canDelete={canEditTeamPunches}
                        onReview={(id, decision) => void reviewInvoice(id, decision)}
                        onDelete={requestDeleteInvoice}
                      />
                    }
                  />
                ) : null}
              </TcAccordionSection>
            </Box>
          ) : null}

          <Box sx={{ mt: 2 }}>
            <TcAccordionSection
              panelId="tc-gestao-just-panel"
              className="tc-hr-just-section"
              title="Justificativas da equipe"
              subtitle={tcAccordionListSubtitle(
                gestaoJustExpanded,
                teamJust.length,
                'justificativa',
                'Nenhuma justificativa neste escopo — expandir para detalhes',
              )}
              expanded={gestaoJustExpanded}
              onToggle={() => setGestaoJustExpanded((prev) => !prev)}
            >
              <Box sx={{ mb: 2 }}>
                <ApprovalStatusFilter
                  ariaLabel="Filtrar justificativas por situação"
                  value={justStatusFilter}
                  options={buildApprovalFilterOptions(justStatusCounts, {
                    approved: 'Aprovadas',
                    rejected: 'Rejeitadas',
                  })}
                  onChange={setJustStatusFilter}
                />
              </Box>
              {filteredTeamJust.length === 0 ? (
                <Typography color="text.secondary">
                  {teamJust.length === 0
                    ? 'Nenhuma justificativa neste escopo.'
                    : 'Nenhuma justificativa neste filtro.'}
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {filteredTeamJust.map((j) => (
                    <Paper key={j.id} sx={{ p: 2, border: 1, borderColor: 'divider' }}>
                      <Typography variant="body2">
                        <strong>{memberNameMap[j.userId] || j.userId}</strong> · {formatDateBr(j.workDate)} ·{' '}
                        {statusJustPt[j.status]}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {j.description}
                      </Typography>
                      {j.status === 'sent' || j.status === 'under_review' ? (
                        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => void reviewJust(j.id, 'approved')}
                          >
                            Aprovar
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => void reviewJust(j.id, 'rejected')}
                          >
                            Rejeitar
                          </Button>
                        </Stack>
                      ) : null}
                    </Paper>
                  ))}
                </Stack>
              )}
            </TcAccordionSection>
          </Box>

          <Box sx={{ mt: 2 }}>
            <TcAccordionSection
              panelId="tc-gestao-bank-panel"
              className="tc-hr-bank-section"
              title="Solicitações de banco de horas"
              subtitle={tcAccordionListSubtitle(
                gestaoBankExpanded,
                teamBank.length,
                'solicitação',
                'Nenhuma solicitação de banco neste escopo — expandir para detalhes',
              )}
              expanded={gestaoBankExpanded}
              onToggle={() => setGestaoBankExpanded((prev) => !prev)}
            >
              <Box sx={{ mb: 2 }}>
                <ApprovalStatusFilter
                  ariaLabel="Filtrar banco de horas por situação"
                  value={bankStatusFilter}
                  options={buildApprovalFilterOptions(bankStatusCounts, {
                    approved: 'Aprovadas',
                    rejected: 'Rejeitadas',
                  })}
                  onChange={setBankStatusFilter}
                />
              </Box>
              {filteredTeamBank.length === 0 ? (
                <Typography color="text.secondary">
                  {teamBank.length === 0
                    ? 'Nenhuma solicitação de banco neste escopo.'
                    : 'Nenhuma solicitação neste filtro.'}
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {filteredTeamBank.map((r) => (
                    <Paper key={r.id} sx={{ p: 2, border: 1, borderColor: 'divider' }}>
                      <Typography variant="body2">
                        <strong>{memberNameMap[r.userId] || r.userId}</strong> · {formatMinutes(r.minutes)} ·{' '}
                        {formatBankCompensationLabel(r)} · {statusBankPt[r.status]}
                      </Typography>
                      {r.status === 'pending' ? (
                        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => void reviewBank(r.id, 'approved')}
                          >
                            Aprovar
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => void reviewBank(r.id, 'rejected')}
                          >
                            Rejeitar
                          </Button>
                        </Stack>
                      ) : null}
                    </Paper>
                  ))}
                </Stack>
              )}
            </TcAccordionSection>
          </Box>

          <Typography variant="subtitle1" component="h3" sx={subsectionTitleSx}>
            Consolidado mensal — elegibilidade bônus
          </Typography>
          {monthlyBonusLoading && monthlyBonusItems.length === 0 ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <CircularProgress size={18} />
              <Typography color="text.secondary">Carregando…</Typography>
            </Stack>
          ) : !monthlyBonusLoading && monthlyBonusPagination.total === 0 ? (
            <Typography color="text.secondary">Nenhum colaborador no escopo para este mês.</Typography>
          ) : monthlyBonusItems.length > 0 ? (
            <>
              <TableContainer
                component={Paper}
                sx={{ overflowX: 'auto', border: 1, borderColor: 'divider', opacity: monthlyBonusLoading ? 0.6 : 1 }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Colaborador</TableCell>
                      <TableCell>Bônus</TableCell>
                      <TableCell>Injustificados</TableCell>
                      <TableCell>Motivo</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {monthlyBonusItems.map((c) => (
                      <TableRow key={c.userId}>
                        <TableCell>{c.name}</TableCell>
                        <TableCell>
                          <StatusChip
                            label={c.bonus.eligible ? 'Elegível' : 'Não elegível'}
                            tone={c.bonus.eligible ? 'success' : 'danger'}
                          />
                        </TableCell>
                        <TableCell>{formatBonusUnjustifiedCounter(c.bonus)}</TableCell>
                        <TableCell>{!c.bonus.eligible && c.bonus.reason ? c.bonus.reason : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TcPagination
                page={monthlyBonusPagination.page}
                totalPages={monthlyBonusPagination.totalPages}
                total={monthlyBonusPagination.total}
                pageSize={monthlyBonusPagination.pageSize}
                onPageChange={setMonthlyBonusPage}
                disabled={monthlyBonusLoading}
                itemLabel="colaboradores"
                onPageSizeChange={(size) => {
                  setMonthlyBonusPageSize(size);
                  setMonthlyBonusPage(1);
                }}
              />
            </>
          ) : null}
        </Paper>
      ) : null}

      {/* Modal: batidas do dia (Corrigir batidas) */}
      <ConfirmModalDayPunches
        open={dayPunchesModalWorkDate !== null}
        workDate={dayPunchesModalWorkDate}
        loading={teamGestaoLoading}
        entries={teamEntriesForDayModal}
        onEdit={openAdminEditEntry}
        onClose={() => setDayPunchesModalWorkDate(null)}
      />

      {/* Modal: editar / criar batida */}
      <EditCreateEntryModal
        open={Boolean(editingTeamEntry || creatingTeamEntryKind)}
        isEditing={Boolean(editingTeamEntry)}
        editingTeamEntry={editingTeamEntry}
        memberName={
          memberNameMap[editingTeamEntry?.userId ?? teamUserId] ||
          editingTeamEntry?.userId ||
          teamUserId
        }
        editEntryWorkDate={editEntryWorkDate}
        editEntryTime={editEntryTime}
        editEntryKind={editEntryKind}
        editEntrySaving={editEntrySaving}
        teamEntryDeleteInProgress={teamEntryDeleteInProgress}
        teamPunchForMemberInProgress={teamPunchForMemberInProgress}
        canDeleteEditingTeamEntry={canDeleteEditingTeamEntry}
        entryDeleteTargetId={entryDeleteTarget?.id ?? null}
        onWorkDateChange={setEditEntryWorkDate}
        onTimeChange={setEditEntryTime}
        onKindChange={handleAdminEntryKindChange}
        onDelete={() => editingTeamEntry && requestDeleteTeamEntry(editingTeamEntry)}
        onSave={() => void saveAdminTeamEntry()}
        onCreate={() => void saveCreateTeamEntry()}
        onClose={() => (editingTeamEntry ? closeAdminEditEntry() : closeCreateTeamEntry())}
      />

      <ConfirmModal
        isOpen={entryDeleteTarget !== null}
        title="Excluir batida"
        message="Excluir esta batida de ponto? O resumo do dia e o banco de horas serão recalculados."
        confirmLabel={teamEntryDeleteInProgress ? 'Excluindo…' : 'Excluir'}
        onConfirm={() => void confirmDeleteTeamEntry()}
        onCancel={cancelDeleteTeamEntry}
        confirmDisabled={teamEntryDeleteInProgress}
        tone="danger"
      />

      <ConfirmModal
        isOpen={hrCorrectionDeleteTargetId !== null}
        title="Excluir solicitação de correção"
        message="Excluir esta solicitação? Se já estiver aprovada, a batida aplicada ao ponto será desfeita."
        confirmLabel={hrCorrectionDeleteInProgress ? 'Excluindo…' : 'Excluir'}
        onConfirm={() => void confirmDeleteHrCorrection()}
        onCancel={cancelDeleteHrCorrection}
        confirmDisabled={hrCorrectionDeleteInProgress}
        tone="danger"
      />

      <ConfirmModal
        isOpen={invoiceDeleteTargetId !== null}
        title="Excluir nota fiscal"
        message="Excluir esta nota fiscal enviada? O arquivo anexo também será removido."
        confirmLabel={invoiceDeleteInProgress ? 'Excluindo…' : 'Excluir'}
        onConfirm={() => void confirmDeleteInvoice()}
        onCancel={cancelDeleteInvoice}
        confirmDisabled={invoiceDeleteInProgress}
        tone="danger"
      />
    </PageContainer>
  );
};

/** Modal «Corrigir batidas» — lista as batidas de um dia com ação de editar. */
const ConfirmModalDayPunches: React.FC<{
  open: boolean;
  workDate: string | null;
  loading: boolean;
  entries: TimeClockEntry[];
  onEdit: (entry: TimeClockEntry) => void;
  onClose: () => void;
}> = ({ open, workDate, loading, entries, onEdit, onClose }) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
    <DialogTitle sx={{ pr: 6 }}>
      Batidas em {workDate ? formatYmdDashToBr(workDate) : ''}
      <IconButton aria-label="Fechar" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers>
      {loading ? (
        <Typography color="text.secondary">Carregando batidas…</Typography>
      ) : entries.length === 0 ? (
        <Typography color="text.secondary">Nenhuma batida neste dia.</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto', border: 1, borderColor: 'divider' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Hora</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell align="right">Ação</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((en) => {
                const br = isoToBrazilDateAndTime(en.occurredAt);
                return (
                  <TableRow key={en.id}>
                    <TableCell>{br.timeHM}</TableCell>
                    <TableCell>{formatTimeclockEntryKind(en.kind)}</TableCell>
                    <TableCell align="right">
                      <Button variant="contained" size="small" onClick={() => onEdit(en)}>
                        Editar
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button variant="outlined" onClick={onClose}>
        Fechar
      </Button>
    </DialogActions>
  </Dialog>
);

/** Modal de edição / criação de batida de ponto (admin/RH). */
const EditCreateEntryModal: React.FC<{
  open: boolean;
  isEditing: boolean;
  editingTeamEntry: TimeClockEntry | null;
  memberName: string;
  editEntryWorkDate: string;
  editEntryTime: string;
  editEntryKind: TimeClockEntryKind;
  editEntrySaving: boolean;
  teamEntryDeleteInProgress: boolean;
  teamPunchForMemberInProgress: boolean;
  canDeleteEditingTeamEntry: boolean;
  entryDeleteTargetId: string | null;
  onWorkDateChange: (v: string) => void;
  onTimeChange: (v: string) => void;
  onKindChange: (v: TimeClockEntryKind) => void;
  onDelete: () => void;
  onSave: () => void;
  onCreate: () => void;
  onClose: () => void;
}> = ({
  open,
  isEditing,
  editingTeamEntry,
  memberName,
  editEntryWorkDate,
  editEntryTime,
  editEntryKind,
  editEntrySaving,
  teamEntryDeleteInProgress,
  teamPunchForMemberInProgress,
  canDeleteEditingTeamEntry,
  entryDeleteTargetId,
  onWorkDateChange,
  onTimeChange,
  onKindChange,
  onDelete,
  onSave,
  onCreate,
  onClose,
}) => {
  const busy = editEntrySaving || teamEntryDeleteInProgress || teamPunchForMemberInProgress;
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pr: 6 }}>
        {isEditing ? 'Editar batida de ponto' : 'Registrar batida de ponto'}
        <IconButton
          aria-label="Fechar"
          disabled={busy}
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ mb: 2 }}>
          Colaborador: <strong>{memberName}</strong>
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Data (horário de Brasília)"
              type="date"
              fullWidth
              value={editEntryWorkDate}
              onChange={(e) => onWorkDateChange(e.target.value)}
              disabled={busy}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Hora"
              type="time"
              fullWidth
              value={editEntryTime}
              onChange={(e) => onTimeChange(e.target.value)}
              disabled={busy}
              slotProps={{ htmlInput: { step: 60 } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <PremiumSelect
              id="tc-admin-entry-kind"
              label="Tipo"
              value={editEntryKind}
              options={ENTRY_KIND_OPTIONS}
              onChange={(v) => onKindChange(v as TimeClockEntryKind)}
              disabled={busy}
            />
          </Grid>
        </Grid>
        {editingTeamEntry?.kind === 'check_in' && !canDeleteEditingTeamEntry ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Há saída registrada neste dia. Use o tipo <strong>Saída</strong> para editar ou excluir a saída. A
            entrada pode ser editada, mas só pode ser excluída depois de remover a saída.
          </Alert>
        ) : null}
      </DialogContent>
      {isEditing && editingTeamEntry ? (
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteOutlinedIcon />}
            disabled={editEntrySaving || teamEntryDeleteInProgress || !canDeleteEditingTeamEntry}
            title={
              !canDeleteEditingTeamEntry
                ? 'Exclua a saída deste dia antes de excluir a entrada'
                : undefined
            }
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            {teamEntryDeleteInProgress && entryDeleteTargetId === editingTeamEntry.id
              ? 'Excluindo…'
              : 'Excluir batida'}
          </Button>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              disabled={editEntrySaving || teamEntryDeleteInProgress}
              onClick={onClose}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              disabled={editEntrySaving || teamEntryDeleteInProgress}
              onClick={onSave}
            >
              {editEntrySaving ? 'Salvando…' : 'Salvar'}
            </Button>
          </Stack>
        </DialogActions>
      ) : (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" disabled={teamPunchForMemberInProgress} onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="contained" disabled={teamPunchForMemberInProgress} startIcon={<AccessTimeOutlinedIcon />} onClick={onCreate}>
            {teamPunchForMemberInProgress ? 'Registrando…' : 'Registrar'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default TimeclockPage;
